/* eslint-disable @typescript-eslint/ban-ts-comment -- legacy R3F viewer uses @ts-nocheck until fully typed */
//@ts-nocheck
import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Radio, Slider, Switch, Tooltip } from 'antd';
import {
    NodeIndexOutlined,
    LoadingOutlined,
    ScissorOutlined,
    UndoOutlined,
    ColumnHeightOutlined,
    CompressOutlined,
    BorderOuterOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import * as THREE from 'three';
import { Canvas, createPortal as r3fCreatePortal, useFrame, useLoader, useThree } from '@react-three/fiber';
import { Html, OrbitControls, OrthographicCamera, useCamera, Environment } from '@react-three/drei';
import { TextureLoader } from 'three/src/loaders/TextureLoader.js';
import { LineSegmentsGeometry } from 'three-stdlib';
import { LineMaterial } from 'three-stdlib';
import { LineSegments2 } from 'three-stdlib';

/** R3F compares props by reference; fresh [] or {} each parent render reapplies defaults and resets the camera/orbit on every click/hover setState. */
const CANVAS_GL = { alpha: true, stencil: true };
/** Never tie this object to `activeTool` — a new `style` reference on `<Canvas>` can trigger R3F resize/camera churn on every tool click. Cursor is synced via `gl.domElement` in an effect. */
const CANVAS_STYLE_ABSOLUTE: React.CSSProperties = {
    position: 'absolute',
    width: '100%',
    height: '100%',
};
const ORTHO_CAMERA_POSITION = [-8, 3, 10];
const HEMISPHERE_LIGHT_POSITION = [0, 200, 0];
const DIRECTIONAL_LIGHT_POSITION = [6, 50, -12];
const POINT_LIGHT_POSITION = [10, 10, 10];
/** Portal scene for Viewcube; must stay referentially stable for R3F. */
const VIEWCUBE_VIRTUAL_CAM_POSITION = [0, 0, 100];
const VIEWCUBE_BOX_GEOMETRY_ARGS = [60, 60, 60];

async function fetchJSON<T>(endpoint: string): Promise<T> {
    const res = await fetch(endpoint);
    const text = await res.text();
    if (!res.ok) return Promise.reject(res);
    try {
        return JSON.parse(text) as T;
    } catch {
        const preview = text.replace(/\s+/g, ' ').slice(0, 120);
        return Promise.reject(
            new Error(
                `3D model URL returned non-JSON (${res.status}, ${preview}${
                    text.length > 120 ? '…' : ''
                }). Often HTML login or SPA fallback — check URL, auth, and signed-URL expiry.`
            )
        );
    }
}

type ViewMode = 'solid-edges' | 'solid' | 'wireframe' | 'xray';
type SurfaceStyle = 'flat' | 'metallic';
type ClipPlaneConfig = {
    enabled: boolean;
    axis: 'x' | 'y' | 'z' | 'face';
    value: number;
    faceNormal?: THREE.Vector3;
    faceRange?: { min: number; max: number };
};
type Orientation = {
    offsetFactor: { x: number; y: number; z: number };
    axisAngle: { x: number; y: number; z: number };
};

type BodyDef = { id: string; label: string; shape_ids: string[]; edge_ids: string[]; color: number[] };
type BodyState = {
    id: string;
    label: string;
    color: string;
    visible: boolean;
    shapeIndices: number[];
    edgeIndices: number[];
};

function rgbToHex(r: number, g: number, b: number): string {
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
    return '#' + [clamp(r), clamp(g), clamp(b)].map(v => v.toString(16).padStart(2, '0')).join('');
}

const TOP: Orientation = { offsetFactor: { x: 0, y: 1, z: 0 }, axisAngle: { x: -Math.PI / 2, y: 0, z: 0 } };
const BOTTOM: Orientation = { offsetFactor: { x: 0, y: -1, z: 0 }, axisAngle: { x: Math.PI / 2, y: 0, z: 0 } };
const FRONT: Orientation = { offsetFactor: { x: 0, y: 0, z: 1 }, axisAngle: { x: 0, y: 0, z: 0 } };
const BACK: Orientation = { offsetFactor: { x: 0, y: 0, z: -1 }, axisAngle: { x: 0, y: Math.PI, z: 0 } };
const LEFT: Orientation = { offsetFactor: { x: -1, y: 0, z: 0 }, axisAngle: { x: 0, y: -Math.PI / 2, z: 0 } };
const RIGHT: Orientation = { offsetFactor: { x: 1, y: 0, z: 0 }, axisAngle: { x: 0, y: Math.PI / 2, z: 0 } };
const VIEWCUBE_ORIENTATIONS = [RIGHT, LEFT, TOP, BOTTOM, FRONT, BACK];

/** Root width breakpoints for sidebar layout (px). Below COMPACT_MIN the sidebar collapses for more canvas. */
const VIEWER_LAYOUT_NORMAL_MIN = 1180;
const VIEWER_LAYOUT_COMPACT_MIN = 560;

/** Collapsed options: browser popup size; floating panel uses 80% width / half max height vs previous defaults. */
const OPTIONS_POPUP_WIDTH = 384;
const OPTIONS_POPUP_HEIGHT = 650;

function optionsFloatingPanelWidth(viewportW: number) {
    return Math.round(0.8 * Math.min(440, Math.max(320, viewportW - 48)));
}

/** Copy linked and inline styles from the opener into a popup document so Ant Design / app CSS apply there. */
function copyStylesIntoPopup(doc: Document) {
    if (typeof document === 'undefined') return;
    document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
        const el = doc.createElement('link');
        el.rel = 'stylesheet';
        el.href = (link as HTMLLinkElement).href;
        doc.head.appendChild(el);
    });
    document.querySelectorAll('style').forEach(style => {
        doc.head.appendChild(style.cloneNode(true));
    });
}

/* ── Floor Grid with sections and fade (Z-up, XY plane) ──── */

/** Rounds `raw` to 1, 2, or 5 × 10^n for readable grid spacing. */
function niceMetricStep(raw: number): number {
    if (!Number.isFinite(raw) || raw <= 0) return 0.5;
    const exp = Math.floor(Math.log10(raw));
    const f = raw / Math.pow(10, exp);
    let nf = 1;
    if (f <= 1) nf = 1;
    else if (f <= 2) nf = 2;
    else if (f <= 5) nf = 5;
    else nf = 10;
    return nf * Math.pow(10, exp);
}

/**
 * Grid in local XZ plane at y = bounds.min.y (part “floor”).
 * Must live inside the same <group rotation={extraRotation}> as the meshes so it stays
 * aligned when the model is rotated; bounds are merged in geometry local space.
 */
function createGridGeometries(bounds: { min: THREE.Vector3; max: THREE.Vector3 }) {
    const sx = Math.max(bounds.max.x - bounds.min.x, 1e-6);
    const sz = Math.max(bounds.max.z - bounds.min.z, 1e-6);
    const maxSpan = Math.max(sx, sz);
    const cellSize = niceMetricStep(maxSpan / 16);
    const sectionEvery = 10;

    const pad = Math.max(maxSpan * 0.18, cellSize * 8);
    const halfX = sx / 2 + pad;
    const halfZ = sz / 2 + pad;
    const nx = Math.max(10, Math.ceil(halfX / cellSize));
    const nz = Math.max(10, Math.ceil(halfZ / cellSize));

    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cz = (bounds.min.z + bounds.max.z) / 2;
    const floorY = bounds.min.y;

    const cellPts: THREE.Vector3[] = [];
    const sectionPts: THREE.Vector3[] = [];

    for (let i = -nx; i <= nx; i++) {
        const arr = i % sectionEvery === 0 ? sectionPts : cellPts;
        const x = cx + i * cellSize;
        arr.push(new THREE.Vector3(x, floorY, cz - nz * cellSize));
        arr.push(new THREE.Vector3(x, floorY, cz + nz * cellSize));
    }
    for (let i = -nz; i <= nz; i++) {
        const arr = i % sectionEvery === 0 ? sectionPts : cellPts;
        const z = cz + i * cellSize;
        arr.push(new THREE.Vector3(cx - nx * cellSize, floorY, z));
        arr.push(new THREE.Vector3(cx + nx * cellSize, floorY, z));
    }

    return {
        cells: new THREE.BufferGeometry().setFromPoints(cellPts),
        sections: new THREE.BufferGeometry().setFromPoints(sectionPts),
    };
}

const FloorGridGroup: FC<{ bounds: { min: THREE.Vector3; max: THREE.Vector3 } }> = ({ bounds }) => {
    const grids = useMemo(() => createGridGeometries(bounds), [bounds]);
    return (
        <group>
            <lineSegments geometry={grids.cells}>
                <lineBasicMaterial color="#6f6f6f" transparent opacity={0.32} />
            </lineSegments>
            <lineSegments geometry={grids.sections}>
                <lineBasicMaterial color="#8a8a8a" transparent opacity={0.48} />
            </lineSegments>
        </group>
    );
};

/* ── Clipping ────────────────────────────────────────────── */

/**
 * Model is rotated -PI/2 on X: model(x,y,z) → world(x, z, -y)
 * So clipping normals in world space:
 *   model X → world X: normal(-1, 0, 0)
 *   model Y → world -Z: normal(0, 0, 1)
 *   model Z → world Y: normal(0, -1, 0)
 */
/**
 * Clipping directions (model rotated -PI/2 on X: model(x,y,z) → world(x, z, -y)):
 *   X: back to front → clips where model_x < value → world: normal(1,0,0), constant=-value
 *   Y: left to right → clips where model_y < value → world: normal(0,0,-1), constant=-value
 *   Z: top to bottom → clips where model_z > value → world: normal(0,-1,0), constant=value
 */
function getClipInitialValue(
    axis: 'x' | 'y' | 'z' | 'face',
    bounds: { min: THREE.Vector3; max: THREE.Vector3 },
    faceRange?: { min: number; max: number }
): number {
    if (axis === 'face' && faceRange) return faceRange.max;
    if (axis === 'face') return 0;
    return bounds.max[axis];
}

function collectCoplanarFace(
    geo: THREE.BufferGeometry,
    faceNormal: THREE.Vector3,
    clickedFace: { a: number; b: number; c: number }
): THREE.BufferGeometry | null {
    const pos = geo.attributes.position;
    const idx = geo.index;
    const ca = new THREE.Vector3().fromBufferAttribute(pos, clickedFace.a);
    const cb = new THREE.Vector3().fromBufferAttribute(pos, clickedFace.b);
    const cc = new THREE.Vector3().fromBufferAttribute(pos, clickedFace.c);
    const clickedCenter = new THREE.Vector3().add(ca).add(cb).add(cc).divideScalar(3);
    const clickedPlaneD = faceNormal.dot(clickedCenter);
    const triCount = idx ? idx.count / 3 : pos.count / 3;
    const a = new THREE.Vector3(),
        b = new THREE.Vector3(),
        c = new THREE.Vector3();
    const triNormal = new THREE.Vector3(),
        ab = new THREE.Vector3(),
        ac = new THREE.Vector3();
    const verts: number[] = [];
    geo.computeBoundingBox();
    const maxDim = geo.boundingBox
        ? Math.max(
              geo.boundingBox.max.x - geo.boundingBox.min.x,
              geo.boundingBox.max.y - geo.boundingBox.min.y,
              geo.boundingBox.max.z - geo.boundingBox.min.z
          )
        : 100;
    for (let i = 0; i < triCount; i++) {
        const i0 = idx ? idx.getX(i * 3) : i * 3;
        const i1 = idx ? idx.getX(i * 3 + 1) : i * 3 + 1;
        const i2 = idx ? idx.getX(i * 3 + 2) : i * 3 + 2;
        a.fromBufferAttribute(pos, i0);
        b.fromBufferAttribute(pos, i1);
        c.fromBufferAttribute(pos, i2);
        ab.subVectors(b, a);
        ac.subVectors(c, a);
        triNormal.crossVectors(ab, ac);
        const len = triNormal.length();
        if (len === 0) continue;
        triNormal.divideScalar(len);
        if (Math.abs(triNormal.dot(faceNormal)) < 0.99) continue;
        const triCenter = new THREE.Vector3().addVectors(a, b).add(c).divideScalar(3);
        if (Math.abs(faceNormal.dot(triCenter) - clickedPlaneD) > maxDim * 0.01) continue;
        verts.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    }
    if (verts.length === 0) return null;
    const hlGeo = new THREE.BufferGeometry();
    hlGeo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    return hlGeo;
}

function projectBoundsOnNormal(
    bounds: { min: THREE.Vector3; max: THREE.Vector3 },
    normal: THREE.Vector3
): { min: number; max: number } {
    const corners = [
        new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
        new THREE.Vector3(bounds.max.x, bounds.min.y, bounds.min.z),
        new THREE.Vector3(bounds.min.x, bounds.max.y, bounds.min.z),
        new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.min.z),
        new THREE.Vector3(bounds.min.x, bounds.min.y, bounds.max.z),
        new THREE.Vector3(bounds.max.x, bounds.min.y, bounds.max.z),
        new THREE.Vector3(bounds.min.x, bounds.max.y, bounds.max.z),
        new THREE.Vector3(bounds.max.x, bounds.max.y, bounds.max.z),
    ];
    const projections = corners.map(c => c.dot(normal));
    return { min: Math.min(...projections), max: Math.max(...projections) };
}

function buildClipPlane(clip: ClipPlaneConfig, rotation: [number, number, number]): THREE.Plane {
    let normal: THREE.Vector3;
    let constant: number;
    if (clip.axis === 'face' && clip.faceNormal) {
        normal = clip.faceNormal.clone().negate();
        constant = clip.value;
        const matrix = new THREE.Matrix4().makeRotationFromEuler(
            new THREE.Euler(rotation[0], rotation[1], rotation[2])
        );
        const plane = new THREE.Plane(normal, constant);
        plane.applyMatrix4(matrix);
        return plane;
    }
    switch (clip.axis) {
        case 'x':
            normal = new THREE.Vector3(-1, 0, 0);
            constant = clip.value;
            break;
        case 'y':
            normal = new THREE.Vector3(0, -1, 0);
            constant = clip.value;
            break;
        case 'z':
        default:
            normal = new THREE.Vector3(0, 0, -1);
            constant = clip.value;
            break;
    }
    const plane = new THREE.Plane(normal, constant);
    const matrix = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rotation[0], rotation[1], rotation[2]));
    plane.applyMatrix4(matrix);
    return plane;
}

const EnableLocalClipping: FC = () => {
    const { gl } = useThree();
    useEffect(() => {
        gl.localClippingEnabled = true;
    }, [gl]);
    return null;
};

/**
 * Visual indicator of the clipping plane position - a semi-transparent colored plane.
 * Positioned in world space; model is rotated -PI/2 on X so:
 *   model X → world X, model Y → world -Z, model Z → world Y
 */
/**
 * Visual plane indicator matching the clipping position.
 * World coordinates after model rotation -PI/2 on X:
 *   model X=v → world pos (v, centerWorldY, centerWorldZ), plane faces X
 *   model Y=v → world pos (centerWorldX, centerWorldY, -v), plane faces Z
 *   model Z=v → world pos (centerWorldX, v, centerWorldZ), plane faces Y
 */
const ClipPlaneVisual: FC<{
    clip: ClipPlaneConfig;
    bounds: { min: THREE.Vector3; max: THREE.Vector3 } | null;
    visible: boolean;
}> = ({ clip, bounds, visible }) => {
    if (!clip.enabled || !bounds || !visible) return null;

    const sx = bounds.max.x - bounds.min.x;
    const sy = bounds.max.y - bounds.min.y;
    const sz = bounds.max.z - bounds.min.z;
    const planeSize = Math.max(sx, sy, sz) * 1.5;

    const wcx = (bounds.min.x + bounds.max.x) / 2;
    const wcy = (bounds.min.z + bounds.max.z) / 2;
    const wcz = -(bounds.min.y + bounds.max.y) / 2;

    let position: [number, number, number];
    let rotation: [number, number, number];

    switch (clip.axis) {
        case 'x':
            position = [clip.value, wcy, wcz];
            rotation = [0, Math.PI / 2, 0];
            break;
        case 'y':
            position = [wcx, wcy, -clip.value];
            rotation = [0, 0, 0];
            break;
        case 'z':
            position = [wcx, clip.value, wcz];
            rotation = [Math.PI / 2, 0, 0];
            break;
    }

    return (
        <mesh position={position} rotation={rotation}>
            <planeGeometry args={[planeSize, planeSize]} />
            <meshBasicMaterial color="#ff2222" transparent opacity={0.15} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
    );
};

/* ── Stencil-based Section Cap (flat plane, no mesh artifacts) ── */

const StencilCapPlane: FC<{
    clip: ClipPlaneConfig;
    bounds: { min: THREE.Vector3; max: THREE.Vector3 };
    color?: string;
}> = ({ clip, bounds, color }) => {
    const planeSize =
        Math.max(bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z) * 3;
    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cy = (bounds.min.y + bounds.max.y) / 2;
    const cz = (bounds.min.z + bounds.max.z) / 2;
    const meshRef = useRef<THREE.Mesh>(null);

    const isFace = clip.axis === 'face' && clip.faceNormal;

    useEffect(() => {
        if (!meshRef.current || !isFace) return;
        const n = clip.faceNormal!;
        const center = new THREE.Vector3(cx, cy, cz);
        const cProj = center.dot(n);
        const pos = center.clone().addScaledVector(n, clip.value - cProj);
        meshRef.current.position.copy(pos);
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
        meshRef.current.quaternion.copy(q);
    }, [clip.value, clip.faceNormal, isFace, cx, cy, cz]);

    let position: [number, number, number] | undefined;
    let rotation: [number, number, number] | undefined;
    if (!isFace) {
        switch (clip.axis) {
            case 'x':
                position = [clip.value, cy, cz];
                rotation = [0, Math.PI / 2, 0];
                break;
            case 'y':
                position = [cx, clip.value, cz];
                rotation = [-Math.PI / 2, 0, 0];
                break;
            case 'z':
                position = [cx, cy, clip.value];
                rotation = [0, 0, 0];
                break;
        }
    }

    return (
        <mesh ref={meshRef} position={position} rotation={rotation} renderOrder={2}>
            <planeGeometry args={[planeSize, planeSize]} />
            <meshBasicMaterial
                color={color || '#f6f6f6'}
                side={THREE.DoubleSide}
                stencilWrite
                stencilFunc={THREE.NotEqualStencilFunc}
                stencilRef={0}
                stencilFail={THREE.ReplaceStencilOp}
                stencilZFail={THREE.ReplaceStencilOp}
                stencilZPass={THREE.ReplaceStencilOp}
            />
        </mesh>
    );
};

/* ── Shape / Edge (Aluminum 6061 values from QuickTurn DB) ── */

const FatEdges: FC<{ geometry: THREE.EdgesGeometry; clipPlanes: THREE.Plane[] }> = ({ geometry, clipPlanes }) => {
    const ref = useRef<any>();

    const fatGeo = useMemo(() => {
        const pos = geometry.attributes.position;
        const arr = [];
        for (let i = 0; i < pos.count; i++) {
            arr.push(pos.getX(i), pos.getY(i), pos.getZ(i));
        }
        const g = new LineSegmentsGeometry();
        g.setPositions(arr);
        return g;
    }, [geometry]);

    const mat = useMemo(() => {
        const m = new LineMaterial({ color: 0xd0d0d0, linewidth: 1 });
        m.clippingPlanes = clipPlanes;
        return m;
    }, []);

    useEffect(() => {
        mat.clippingPlanes = clipPlanes;
    }, [clipPlanes]);

    useFrame(() => {
        if (ref.current) ref.current.material.resolution.set(window.innerWidth, window.innerHeight);
    });

    return <primitive ref={ref} object={new LineSegments2(fatGeo, mat)} />;
};

/** Flat = legible, poco brillo; Metallic = acabado más vivo sin volver a espejo extremo. */
function surfaceParams(style: SurfaceStyle) {
    if (style === 'flat') {
        return { metalness: 0.12, roughness: 0.78, envMapIntensity: 0.38 };
    }
    return { metalness: 0.72, roughness: 0.28, envMapIntensity: 0.82 };
}

const Shape: FC<{
    shape: any;
    viewMode: ViewMode;
    clipPlanes: THREE.Plane[];
    showCap: boolean;
    bodyColor?: string;
    surfaceStyle: SurfaceStyle;
}> = ({ shape, viewMode, clipPlanes, showCap, bodyColor, surfaceStyle }) => {
    const matRef = useRef<THREE.MeshPhysicalMaterial>(null);
    const edgesGeo = useMemo(() => new THREE.EdgesGeometry(shape, 5), [shape]);
    const isWireframe = viewMode === 'wireframe';
    const hasClip = clipPlanes.length > 0;
    const color = bodyColor || '#f6f6f6';
    const renderStencil = hasClip && showCap && !isWireframe;
    const surf = surfaceParams(surfaceStyle);

    useEffect(() => {
        if (!matRef.current) return;
        const m = matRef.current;
        m.transparent = viewMode === 'xray';
        m.opacity = viewMode === 'xray' ? 0.25 : 1;
        m.visible = !isWireframe;
        m.clippingPlanes = clipPlanes;
        m.side = renderStencil ? THREE.FrontSide : THREE.DoubleSide;
        m.metalness = surf.metalness;
        m.roughness = surf.roughness;
        m.envMapIntensity = surf.envMapIntensity;
        m.needsUpdate = true;
    }, [viewMode, clipPlanes, renderStencil, surf.metalness, surf.roughness, surf.envMapIntensity]);

    return (
        <group>
            <mesh geometry={shape} castShadow>
                <meshPhysicalMaterial
                    ref={matRef}
                    color={color}
                    metalness={surf.metalness}
                    roughness={surf.roughness}
                    envMapIntensity={surf.envMapIntensity}
                    side={renderStencil ? THREE.FrontSide : THREE.DoubleSide}
                    clippingPlanes={clipPlanes}
                />
            </mesh>
            {renderStencil && (
                <>
                    <mesh geometry={shape} renderOrder={1}>
                        <meshBasicMaterial
                            depthWrite={false}
                            depthTest={false}
                            colorWrite={false}
                            stencilWrite
                            stencilFunc={THREE.AlwaysStencilFunc}
                            stencilFail={THREE.KeepStencilOp}
                            stencilZFail={THREE.DecrementWrapStencilOp}
                            stencilZPass={THREE.DecrementWrapStencilOp}
                            side={THREE.FrontSide}
                            clippingPlanes={clipPlanes}
                        />
                    </mesh>
                    <mesh geometry={shape} renderOrder={1}>
                        <meshBasicMaterial
                            depthWrite={false}
                            depthTest={false}
                            colorWrite={false}
                            stencilWrite
                            stencilFunc={THREE.AlwaysStencilFunc}
                            stencilFail={THREE.KeepStencilOp}
                            stencilZFail={THREE.IncrementWrapStencilOp}
                            stencilZPass={THREE.IncrementWrapStencilOp}
                            side={THREE.BackSide}
                            clippingPlanes={clipPlanes}
                        />
                    </mesh>
                </>
            )}
            {isWireframe && <FatEdges geometry={edgesGeo} clipPlanes={clipPlanes} />}
        </group>
    );
};

const ModelEdge: FC<{ edge: any; visible: boolean; clipPlanes: THREE.Plane[] }> = ({ edge, visible, clipPlanes }) => {
    if (!visible) return null;
    return (
        <line geometry={edge}>
            <lineBasicMaterial color="#555555" linewidth={1.5} clippingPlanes={clipPlanes} />
        </line>
    );
};

/* ── ViewCube Edges ──────────────────────────────────────── */

function ViewcubeEdges({ color = 'black', threshold = 15, scale = 1, ...props }: any) {
    const ref = React.useRef<THREE.LineSegments>(null!);
    React.useLayoutEffect(() => {
        const parent = ref.current.parent as THREE.Mesh;
        if (parent) {
            const geom = parent.geometry;
            if (geom !== ref.current.userData.currentGeom) {
                ref.current.userData.currentGeom = geom;
                ref.current.geometry = new THREE.EdgesGeometry(geom, threshold);
            }
        }
    });
    return (
        <lineSegments ref={ref} raycast={() => null} scale={scale} {...props}>
            <lineBasicMaterial color={color} />
        </lineSegments>
    );
}

/* ── Scene Axes Indicator (arrows + labels at model origin) ── */

const AxisLabelSprite: FC<{ text: string; position: [number, number, number]; color: string; scale: number }> = ({
    text,
    position,
    color,
    scale: s,
}) => {
    const texture = useMemo(() => {
        const sz = 128;
        const canvas = document.createElement('canvas');
        canvas.width = sz;
        canvas.height = sz;
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, sz, sz);
        ctx.font = 'bold 96px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = color;
        ctx.fillText(text, sz / 2, sz / 2);
        const tex = new THREE.CanvasTexture(canvas);
        tex.needsUpdate = true;
        return tex;
    }, [text, color]);

    return (
        <sprite position={position} scale={[s, s, 1]}>
            <spriteMaterial map={texture} transparent depthTest={false} />
        </sprite>
    );
};

const SceneAxesIndicator: FC<{ bounds: { min: THREE.Vector3; max: THREE.Vector3 } }> = ({ bounds }) => {
    const groupRef = useRef<THREE.Group>(null!);

    const { origin, len, labelScale } = useMemo(() => {
        const size = new THREE.Vector3();
        new THREE.Box3(bounds.min, bounds.max).getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const axisLen = maxDim * 0.4;
        return {
            origin: new THREE.Vector3(
                bounds.min.x - maxDim * 0.2,
                bounds.min.y - maxDim * 0.2,
                bounds.min.z - maxDim * 0.2
            ),
            len: axisLen,
            labelScale: axisLen * 0.25,
        };
    }, [bounds]);

    const arrows = useMemo(() => {
        const headLen = len * 0.15;
        const headW = len * 0.06;
        return [
            new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), new THREE.Vector3(), len, 0xff4444, headLen, headW),
            new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), new THREE.Vector3(), len, 0x44cc44, headLen, headW),
            new THREE.ArrowHelper(new THREE.Vector3(0, 0, 1), new THREE.Vector3(), len, 0x4488ff, headLen, headW),
        ];
    }, [len]);

    useEffect(
        () => () =>
            arrows.forEach(a => {
                a.line.geometry.dispose();
                a.cone.geometry.dispose();
            }),
        [arrows]
    );

    const labelOffset = len * 1.12;

    return (
        <group ref={groupRef} position={origin}>
            {arrows.map((a, i) => (
                <primitive key={i} object={a} />
            ))}
            <AxisLabelSprite text="X" position={[labelOffset, 0, 0]} color="#ff4444" scale={labelScale} />
            <AxisLabelSprite text="Y" position={[0, labelOffset, 0]} color="#44cc44" scale={labelScale} />
            <AxisLabelSprite text="Z" position={[0, 0, labelOffset]} color="#4488ff" scale={labelScale} />
        </group>
    );
};

/* ── ViewCube ────────────────────────────────────────────── */

const Viewcube: FC<{ orbitTargetRef: React.MutableRefObject<THREE.Vector3> }> = ({ orbitTargetRef }) => {
    const { gl, scene, camera, size } = useThree();
    const virtualScene = useMemo(() => new THREE.Scene(), []);
    const virtualCam = useRef();
    const ref = useRef();
    const [hover, set] = useState(null);
    const matrix = new THREE.Matrix4();
    const tex = (name: string) => useLoader(TextureLoader, `/textures/${name}.jpg`);
    const tf = tex('front'),
        tfh = tex('front-hover');
    const tb = tex('back'),
        tbh = tex('back-hover');
    const tt = tex('top'),
        tth = tex('top-hover');
    const tbt = tex('bottom'),
        tbth = tex('bottom-hover');
    const tl = tex('left'),
        tlh = tex('left-hover');
    const tr = tex('right'),
        trh = tex('right-hover');

    const cubeMeshPosition = useMemo(() => [size.width / 2 - 80, size.height / 2 - 80, 0], [size.width, size.height]);

    useFrame(() => {
        matrix.copy(camera.matrix).invert();
        ref.current.quaternion.setFromRotationMatrix(matrix);
        gl.autoClear = true;
        gl.render(scene, camera);
        gl.autoClear = false;
        gl.clearDepth();
        const savedClipPlanes = gl.clippingPlanes;
        gl.clippingPlanes = [];
        gl.render(virtualScene, virtualCam.current);
        gl.clippingPlanes = savedClipPlanes;
    }, 1);

    const navigateTo = (o: Orientation) => {
        const center = orbitTargetRef.current;
        const d = camera.position.distanceTo(center);
        camera.position.set(
            center.x + d * o.offsetFactor.x,
            center.y + d * o.offsetFactor.y,
            center.z + d * o.offsetFactor.z
        );
        camera.lookAt(center);
    };

    /** useCamera(virtualCam) calls setFromCamera(..., virtualCam.current); ref can be null before mount → crash on any canvas hit-test */
    const virtualCamRaycast = useCamera(virtualCam);
    const safeVirtualCamRaycast = useCallback(
        function (raycaster, intersects) {
            const cam = virtualCam.current;
            if (!cam || !(cam instanceof THREE.Camera)) return;
            virtualCamRaycast.call(this, raycaster, intersects);
        },
        [virtualCamRaycast]
    );

    return r3fCreatePortal(
        <>
            <OrthographicCamera ref={virtualCam} makeDefault={false} position={VIEWCUBE_VIRTUAL_CAM_POSITION} />
            <mesh
                ref={ref}
                raycast={safeVirtualCamRaycast}
                position={cubeMeshPosition}
                onClick={e => {
                    const f = Math.floor(e.faceIndex / 2);
                    if (f >= 0 && f < 6) navigateTo(VIEWCUBE_ORIENTATIONS[f]);
                }}
                onPointerOut={() => set(null)}
                onPointerMove={e => set(Math.floor(e.faceIndex / 2))}
            >
                <meshLambertMaterial attachArray="material" map={hover === 0 ? trh : tr} />
                <meshLambertMaterial attachArray="material" map={hover === 1 ? tlh : tl} />
                <meshLambertMaterial attachArray="material" map={hover === 2 ? tth : tt} />
                <meshLambertMaterial attachArray="material" map={hover === 3 ? tbth : tbt} />
                <meshLambertMaterial attachArray="material" map={hover === 4 ? tfh : tf} />
                <meshLambertMaterial attachArray="material" map={hover === 5 ? tbh : tb} />
                <boxGeometry args={VIEWCUBE_BOX_GEOMETRY_ARGS} />
                <ViewcubeEdges color={0x555555} threshold={15} scale={1.002} />
            </mesh>
            <ambientLight intensity={0.5} />
            <pointLight position={POINT_LIGHT_POSITION} intensity={0.5} />
        </>,
        virtualScene
    );
};

/** After JSON load the modal canvas often still has stale R3F `size`; nudge frames so ortho fit sees real dimensions. */
const RefitInvalidator: FC<{ fitEpoch: number; loading: boolean }> = ({ fitEpoch, loading }) => {
    const invalidate = useThree(s => s.invalidate);
    useEffect(() => {
        if (loading || fitEpoch <= 0) return;
        const tick = () => invalidate();
        const t0 = setTimeout(tick, 0);
        const t1 = setTimeout(tick, 80);
        const t2 = setTimeout(tick, 250);
        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(tick);
        });
        return () => {
            clearTimeout(t0);
            clearTimeout(t1);
            clearTimeout(t2);
            cancelAnimationFrame(raf1);
        };
    }, [fitEpoch, loading, invalidate]);
    return null;
};

/**
 * `react-use-measure` (R3F default) frequently misses layout for canvases inside Ant Design modals/drawers.
 * Sync internal `size` from the canvas parent's box so orthographic fit/zoom matches pixels on screen.
 */
const CanvasParentResizeSync: FC = () => {
    const gl = useThree(s => s.gl);
    const setSize = useThree(s => s.setSize);
    const invalidate = useThree(s => s.invalidate);
    useEffect(() => {
        const canvas = gl.domElement;
        const parent = canvas.parentElement;
        if (!parent || typeof ResizeObserver === 'undefined') return;
        const apply = () => {
            const { width, height } = parent.getBoundingClientRect();
            const w = Math.max(1, Math.round(width));
            const h = Math.max(1, Math.round(height));
            setSize(w, h);
            invalidate();
        };
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(parent);
        return () => ro.disconnect();
    }, [gl, setSize, invalidate]);
    return null;
};

/* ── Controls (Z-up) ─────────────────────────────────────── */

const Controls: FC<{
    partRef: any;
    modelShapes: any[];
    fitEpoch?: number;
    onResetRef?: React.MutableRefObject<(() => void) | null>;
    orbitTargetRef?: React.MutableRefObject<THREE.Vector3>;
    navigateRef?: React.MutableRefObject<((o: Orientation) => void) | null>;
    orbitingRef?: React.MutableRefObject<boolean>;
    lastOrbitEndRef?: React.MutableRefObject<number>;
    cameraRef?: React.MutableRefObject<THREE.Camera | null>;
}> = ({
    partRef,
    modelShapes,
    fitEpoch = 0,
    onResetRef,
    orbitTargetRef,
    navigateRef,
    orbitingRef,
    lastOrbitEndRef,
    cameraRef,
}) => {
    const { camera, gl, size } = useThree();
    const orbitControlsArgs = useMemo(() => [camera, gl.domElement], [camera, gl.domElement]);
    useEffect(() => {
        if (cameraRef) cameraRef.current = camera;
    }, [camera, cameraRef]);
    const controlsRef = useRef(null);
    const savedCameraRef = useRef<{ pos: THREE.Vector3; zoom?: number; target: THREE.Vector3 } | null>(null);
    /** Ignore subpixel / layout-thrash size updates from R3F — they re-run zoom math and feel like a full viewer "refresh". */
    const lastOrthoResizeRef = useRef<{ w: number; h: number; fitEpoch: number }>({
        w: -1,
        h: -1,
        fitEpoch: -1,
    });
    const [meta, setMeta] = useState<any>({
        boundingSphere: { center: new THREE.Vector3(), radius: 0 },
        boundingBox: { min: new THREE.Vector3(), max: new THREE.Vector3() },
    });

    /** New model load: drop resize debounce state so the next real layout pass always refits zoom. */
    useEffect(() => {
        lastOrthoResizeRef.current = { w: -1, h: -1, fitEpoch: -1 };
    }, [fitEpoch]);

    useEffect(() => {
        if (partRef.current) {
            const box = new THREE.Box3().setFromObject(partRef.current);
            const sphere = new THREE.Sphere();
            box.getBoundingSphere(sphere);
            setMeta({ boundingSphere: sphere, boundingBox: box });
        }
    }, [modelShapes, partRef, fitEpoch]);

    useEffect(() => {
        if (meta.boundingSphere.radius > 0) {
            const bb = meta.boundingBox;
            const center = new THREE.Vector3();
            const sz = new THREE.Vector3();
            bb.getCenter(center);
            bb.getSize(sz);
            const maxDim = Math.max(sz.x, sz.y, sz.z);
            const dist = maxDim * 6.3;
            camera.position.set(-dist * 0.6, dist * 0.3, dist * 0.8);
            camera.far = dist * 20;

            // Fit orthographic zoom by projecting the bounding box onto the view plane
            const ortho = camera as THREE.OrthographicCamera;
            if (ortho.isOrthographicCamera) {
                camera.lookAt(center);
                camera.updateMatrixWorld();
                if (size.width > 1 && size.height > 1) {
                    const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
                    const camUp = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
                    let minR = Infinity,
                        maxR = -Infinity,
                        minU = Infinity,
                        maxU = -Infinity;
                    for (let i = 0; i < 8; i++) {
                        const corner = new THREE.Vector3(
                            i & 1 ? bb.max.x : bb.min.x,
                            i & 2 ? bb.max.y : bb.min.y,
                            i & 4 ? bb.max.z : bb.min.z
                        );
                        const r = corner.dot(camRight);
                        const u = corner.dot(camUp);
                        if (r < minR) minR = r;
                        if (r > maxR) maxR = r;
                        if (u < minU) minU = u;
                        if (u > maxU) maxU = u;
                    }
                    const pad = 1.4;
                    const projW = (maxR - minR) * pad;
                    const projH = (maxU - minU) * pad;
                    ortho.zoom = Math.min(size.width / projW, size.height / projH);
                }
            }

            camera.updateProjectionMatrix();
            if (controlsRef.current) {
                controlsRef.current.target.copy(center);
                controlsRef.current.update();
                controlsRef.current.saveState();
                // store the fitted camera state so reset can restore zoom/position reliably
                savedCameraRef.current = {
                    pos: camera.position.clone(),
                    target: center.clone(),
                    zoom: (camera as THREE.OrthographicCamera).isOrthographicCamera
                        ? (camera as THREE.OrthographicCamera).zoom
                        : undefined,
                };
            } else {
                camera.lookAt(center);
            }
            if (orbitTargetRef) orbitTargetRef.current = center.clone();
        }
    }, [partRef, meta.boundingSphere.radius, fitEpoch]);

    // Canvas size changes when the options sidebar opens (layout reflow). Do not rerun the full fit above —
    // that resets camera/target and feels like a "refresh". Only rescale orthographic zoom for the current view.
    useEffect(() => {
        if (meta.boundingSphere.radius <= 0) return;
        if (size.width <= 1 || size.height <= 1) return;
        const ortho = camera as THREE.OrthographicCamera;
        if (!ortho.isOrthographicCamera || !controlsRef.current) return;
        const prev = lastOrthoResizeRef.current;
        const fitChanged = prev.fitEpoch !== fitEpoch;
        const meaningful = prev.w < 0 || Math.abs(size.width - prev.w) >= 2 || Math.abs(size.height - prev.h) >= 2;
        if (!fitChanged && !meaningful) return;
        lastOrthoResizeRef.current = { w: size.width, h: size.height, fitEpoch };
        const bb = meta.boundingBox;
        camera.updateMatrixWorld();
        const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
        const camUp = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
        let minR = Infinity,
            maxR = -Infinity,
            minU = Infinity,
            maxU = -Infinity;
        for (let i = 0; i < 8; i++) {
            const corner = new THREE.Vector3(
                i & 1 ? bb.max.x : bb.min.x,
                i & 2 ? bb.max.y : bb.min.y,
                i & 4 ? bb.max.z : bb.min.z
            );
            const r = corner.dot(camRight);
            const u = corner.dot(camUp);
            if (r < minR) minR = r;
            if (r > maxR) maxR = r;
            if (u < minU) minU = u;
            if (u > maxU) maxU = u;
        }
        const pad = 1.4;
        const projW = (maxR - minR) * pad;
        const projH = (maxU - minU) * pad;
        if (projW <= 0 || projH <= 0) return;
        ortho.zoom = Math.min(size.width / projW, size.height / projH);
        ortho.updateProjectionMatrix();
        controlsRef.current.update();
        if (savedCameraRef.current) {
            savedCameraRef.current.zoom = ortho.zoom;
        }
    }, [size.width, size.height, meta.boundingSphere.radius, camera, fitEpoch]);

    useEffect(() => {
        if (onResetRef) {
            onResetRef.current = () => {
                const ortho = camera as THREE.OrthographicCamera;
                if (savedCameraRef.current) {
                    const s = savedCameraRef.current;
                    camera.position.copy(s.pos);
                    if (ortho.isOrthographicCamera && s.zoom !== undefined) {
                        ortho.zoom = s.zoom;
                        camera.updateProjectionMatrix();
                    }
                    if (controlsRef.current) {
                        controlsRef.current.target.copy(s.target);
                        controlsRef.current.update();
                    }
                } else if (controlsRef.current) {
                    controlsRef.current.reset();
                }
            };
        }
        if (navigateRef) {
            navigateRef.current = (o: Orientation) => {
                const center = orbitTargetRef?.current || new THREE.Vector3();
                const d = camera.position.distanceTo(center);
                camera.position.set(
                    center.x + d * o.offsetFactor.x,
                    center.y + d * o.offsetFactor.y,
                    center.z + d * o.offsetFactor.z
                );
                camera.lookAt(center);
                if (controlsRef.current) {
                    controlsRef.current.target.copy(center);
                    controlsRef.current.update();
                }
            };
        }
    }, [onResetRef, navigateRef, meta.boundingSphere.radius, fitEpoch]);

    useEffect(() => {
        const el = gl.domElement;
        const handleWheel = (e: WheelEvent) => {
            if (e.shiftKey) return;
            e.preventDefault();
            const ortho = camera as THREE.OrthographicCamera;
            if (!ortho.isOrthographicCamera) return;
            const rect = el.getBoundingClientRect();
            const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            const worldBefore = new THREE.Vector3(ndcX, ndcY, 0).unproject(ortho);
            const factor = e.deltaY > 0 ? 0.9 : 1.1;
            ortho.zoom = Math.max(0.1, ortho.zoom * factor);
            ortho.updateProjectionMatrix();
            const worldAfter = new THREE.Vector3(ndcX, ndcY, 0).unproject(ortho);
            const delta = worldBefore.clone().sub(worldAfter);
            ortho.position.add(delta);
            if (controlsRef.current) {
                controlsRef.current.target.add(delta);
                controlsRef.current.update();
            }
        };
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [camera, gl]);

    return (
        <OrbitControls
            ref={controlsRef}
            enablePan
            enableZoom={false}
            enableDamping
            dampingFactor={0.3}
            minDistance={meta.boundingSphere.radius / 5}
            maxDistance={meta.boundingSphere.radius * 10}
            args={orbitControlsArgs}
            onStart={() => {
                if (orbitingRef) orbitingRef.current = true;
            }}
            onEnd={() => {
                if (orbitingRef) orbitingRef.current = false;
                if (lastOrbitEndRef) lastOrbitEndRef.current = Date.now();
            }}
        />
    );
};

/* ── FatLine (reusable thick line from points) ──────────── */

const FatLine: FC<{ points: THREE.Vector3[]; color: string; width?: number }> = ({ points, color, width = 3 }) => {
    const ref = useRef<any>();
    const { geo, mat } = useMemo(() => {
        if (points.length < 2) return { geo: null, mat: null };
        const pairs: number[] = [];
        for (let i = 0; i < points.length - 1; i++) {
            pairs.push(points[i].x, points[i].y, points[i].z, points[i + 1].x, points[i + 1].y, points[i + 1].z);
        }
        const g = new LineSegmentsGeometry();
        g.setPositions(pairs);
        const m = new LineMaterial({ color: new THREE.Color(color).getHex(), linewidth: width, depthTest: false });
        return { geo: g, mat: m };
    }, [points, color, width]);
    useFrame(() => {
        if (ref.current) ref.current.material.resolution.set(window.innerWidth, window.innerHeight);
    });
    const obj = useMemo(() => {
        if (!geo || !mat) return null;
        const o = new LineSegments2(geo, mat);
        o.raycast = () => {};
        return o;
    }, [geo, mat]);
    if (!obj) return null;
    return <primitive ref={ref} object={obj} renderOrder={999} />;
};

/* ── Tool types ──────────────────────────────────────────── */

type ActiveTool = null | 'ruler' | 'angle' | 'thickness' | 'area' | 'edge' | 'edgeDist';

const labelStyle: React.CSSProperties = {
    background: 'rgba(0,0,0,0.75)',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: 12,
    whiteSpace: 'nowrap' as const,
    backdropFilter: 'blur(4px)',
};

/* ── MeasureTool (multi-measurement) ──────────────────────── */

type Measurement = {
    p1: THREE.Vector3;
    p2: THREE.Vector3;
    line: THREE.BufferGeometry;
    mid: THREE.Vector3;
    dist: number;
};

const MEASURE_COLORS = ['#ff3333', '#33cc66', '#3399ff', '#ff9933', '#cc33ff', '#33cccc', '#ffcc33', '#ff6699'];

const MeasureTool: FC<{
    active: boolean;
    measurements: Measurement[];
    pendingPoint: THREE.Vector3 | null;
    fmt: (mm: number) => string;
}> = ({ active, measurements, pendingPoint, fmt }) => {
    if (!active && measurements.length === 0) return null;
    return (
        <group>
            {measurements.map((m, i) => {
                const color = MEASURE_COLORS[i % MEASURE_COLORS.length];
                return (
                    <group key={i}>
                        <mesh position={m.p1} raycast={() => {}}>
                            <sphereBufferGeometry args={[0.6, 16, 16]} />
                            <meshStandardMaterial color={color} />
                        </mesh>
                        <mesh position={m.p2} raycast={() => {}}>
                            <sphereBufferGeometry args={[0.6, 16, 16]} />
                            <meshStandardMaterial color={color} />
                        </mesh>
                        <FatLine points={[m.p1, m.p2]} color={color} />
                        <Html position={m.mid}>
                            <span style={{ ...labelStyle, borderLeft: `3px solid ${color}` }}>{fmt(m.dist)}</span>
                        </Html>
                    </group>
                );
            })}
            {pendingPoint && (
                <mesh position={pendingPoint} raycast={() => {}}>
                    <sphereBufferGeometry args={[0.6, 16, 16]} />
                    <meshStandardMaterial color={MEASURE_COLORS[measurements.length % MEASURE_COLORS.length]} />
                </mesh>
            )}
        </group>
    );
};

/* ── AngleTool (two edges, 0°–180° between directions) ───── */

type AngleMeasurement = {
    edgeIdx1: number;
    edgeIdx2: number;
    dir1: THREE.Vector3;
    dir2: THREE.Vector3;
    mid1: THREE.Vector3;
    mid2: THREE.Vector3;
    /** If set, both directions radiate from this point (shared vertex). */
    corner: THREE.Vector3 | null;
    angle: number;
    highlight1: THREE.BufferGeometry;
    highlight2: THREE.BufferGeometry;
};

const ANGLE_COLORS = ['#ff8800', '#33cc66', '#3399ff', '#ff3399', '#cccc00', '#33cccc', '#ff3333', '#9966ff'];

const AngleEdgePair: FC<{ m: AngleMeasurement; color: string }> = ({ m, color }) => {
    const { center, extGeo1, extGeo2 } = useMemo(() => {
        const fallback = {
            center: new THREE.Vector3().addVectors(m.mid1, m.mid2).multiplyScalar(0.5),
            extGeo1: null as THREE.BufferGeometry | null,
            extGeo2: null as THREE.BufferGeometry | null,
        };
        try {
            if (m.corner)
                return {
                    center: m.corner.clone(),
                    extGeo1: null as THREE.BufferGeometry | null,
                    extGeo2: null as THREE.BufferGeometry | null,
                };
            const ep1 = edgeEndpoints(m.highlight1);
            const ep2 = edgeEndpoints(m.highlight2);
            if (!ep1 || !ep2) return fallback;
            const s1 = ep1[0],
                e1 = ep1[1],
                s2 = ep2[0],
                e2 = ep2[1];
            const ld1 = new THREE.Vector3().subVectors(e1, s1);
            const ld2 = new THREE.Vector3().subVectors(e2, s2);
            if (ld1.lengthSq() < 1e-12 || ld2.lengthSq() < 1e-12) return fallback;
            ld1.normalize();
            ld2.normalize();
            const w = new THREE.Vector3().subVectors(s1, s2);
            const bv = ld1.dot(ld2),
                dv = ld1.dot(w),
                ev = ld2.dot(w);
            const denom = 1 - bv * bv;
            if (Math.abs(denom) < 1e-10) return fallback;
            const t = (bv * ev - dv) / denom;
            const sv = (ev - bv * dv) / denom;
            const c1 = s1.clone().addScaledVector(ld1, t);
            const c2 = s2.clone().addScaledVector(ld2, sv);
            const vc = c1.clone().add(c2).multiplyScalar(0.5);
            const near1 = ep1[0].distanceToSquared(vc) <= ep1[1].distanceToSquared(vc) ? ep1[0] : ep1[1];
            const near2 = ep2[0].distanceToSquared(vc) <= ep2[1].distanceToSquared(vc) ? ep2[0] : ep2[1];
            const g1 = new THREE.BufferGeometry();
            g1.setAttribute(
                'position',
                new THREE.Float32BufferAttribute([near1.x, near1.y, near1.z, vc.x, vc.y, vc.z], 3)
            );
            const g2 = new THREE.BufferGeometry();
            g2.setAttribute(
                'position',
                new THREE.Float32BufferAttribute([near2.x, near2.y, near2.z, vc.x, vc.y, vc.z], 3)
            );
            return { center: vc, extGeo1: g1, extGeo2: g2 };
        } catch {
            return fallback;
        }
    }, [m.corner, m.mid1, m.mid2, m.highlight1, m.highlight2]);
    useEffect(
        () => () => {
            extGeo1?.dispose();
            extGeo2?.dispose();
        },
        [extGeo1, extGeo2]
    );
    const { arrowLen, arcRadius } = useMemo(() => {
        const a = edgeTotalLength(m.highlight1);
        const b = edgeTotalLength(m.highlight2);
        const shorter = Math.min(a, b);
        return { arrowLen: Math.max(1.2, Math.min(8, shorter * 0.45)), arcRadius: shorter * 0.4 };
    }, [m.highlight1, m.highlight2]);
    const arcGeo = useMemo(() => {
        try {
            const ep1 = edgeEndpoints(m.highlight1);
            const ep2 = edgeEndpoints(m.highlight2);
            if (!ep1 || !ep2) return null;
            const [near1, far1] =
                ep1[0].distanceToSquared(center) <= ep1[1].distanceToSquared(center)
                    ? [ep1[0], ep1[1]]
                    : [ep1[1], ep1[0]];
            const [near2, far2] =
                ep2[0].distanceToSquared(center) <= ep2[1].distanceToSquared(center)
                    ? [ep2[0], ep2[1]]
                    : [ep2[1], ep2[0]];
            const eDir1 = new THREE.Vector3().subVectors(far1, near1);
            const eDir2 = new THREE.Vector3().subVectors(far2, near2);
            if (eDir1.lengthSq() < 1e-12 || eDir2.lengthSq() < 1e-12) return null;
            eDir1.normalize();
            eDir2.normalize();
            const touchPt1 = center.clone().addScaledVector(eDir1, arcRadius);
            const touchPt2 = center.clone().addScaledVector(eDir2, arcRadius);
            const v1 = eDir1;
            const v2 = eDir2;
            const angleRad = Math.acos(Math.min(1, Math.max(-1, v1.dot(v2))));
            if (angleRad < 0.01) return null;
            const axis = new THREE.Vector3().crossVectors(v1, v2);
            if (axis.length() < 1e-6) return null;
            axis.normalize();
            const steps = 48;
            const positions: number[] = [];
            let prev = touchPt1.clone();
            for (let i = 1; i <= steps; i++) {
                const cur =
                    i === steps
                        ? touchPt2.clone()
                        : v1
                              .clone()
                              .applyQuaternion(new THREE.Quaternion().setFromAxisAngle(axis, (i / steps) * angleRad))
                              .multiplyScalar(arcRadius)
                              .add(center);
                positions.push(prev.x, prev.y, prev.z, cur.x, cur.y, cur.z);
                prev = cur;
            }
            const geo = new THREE.BufferGeometry();
            geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
            return geo;
        } catch {
            return null;
        }
    }, [center, m.highlight1, m.highlight2, arcRadius]);
    useEffect(
        () => () => {
            arcGeo?.dispose();
        },
        [arcGeo]
    );
    const labelPos = useMemo(() => {
        const out = center.clone();
        const sum = m.dir1.clone().add(m.dir2);
        if (sum.lengthSq() > 1e-10) {
            out.addScaledVector(sum.normalize(), arcRadius * 1.15);
        } else {
            const perp = new THREE.Vector3().crossVectors(m.dir1, new THREE.Vector3(0, 1, 0));
            if (perp.lengthSq() < 1e-10) perp.crossVectors(m.dir1, new THREE.Vector3(1, 0, 0));
            perp.normalize();
            out.addScaledVector(perp, arcRadius * 1.15);
        }
        return out;
    }, [center, m.dir1, m.dir2, arcRadius]);
    const cnum = parseInt(color.replace('#', ''), 16);
    const o1 = m.corner ?? m.mid1;
    const o2 = m.corner ?? m.mid2;
    return (
        <group>
            <FatEdgeHighlight geometry={m.highlight1} color={color} />
            <FatEdgeHighlight geometry={m.highlight2} color={color} />
            {extGeo1 && <FatEdgeHighlight geometry={extGeo1} color={color} />}
            {extGeo2 && <FatEdgeHighlight geometry={extGeo2} color={color} />}
            <arrowHelper args={[m.dir1, o1, arrowLen, cnum, arrowLen * 0.22, arrowLen * 0.12]} />
            <arrowHelper args={[m.dir2, o2, arrowLen, cnum, arrowLen * 0.22, arrowLen * 0.12]} />
            {arcGeo && <FatEdgeHighlight geometry={arcGeo} color={color} />}
            <Html position={[labelPos.x, labelPos.y, labelPos.z]}>
                <span style={{ ...labelStyle, borderLeft: `3px solid ${color}` }}>{m.angle.toFixed(1)}°</span>
            </Html>
        </group>
    );
};

const AngleTool: FC<{
    active: boolean;
    measurements: AngleMeasurement[];
    pendingIdx: number | null;
    edges: THREE.BufferGeometry[];
}> = ({ active, measurements, pendingIdx, edges }) => {
    if (!active && measurements.length === 0) return null;
    return (
        <group>
            {active && pendingIdx !== null && edges[pendingIdx] && (
                <FatEdgeHighlight geometry={edges[pendingIdx]} color="#00e5ff" />
            )}
            {measurements.map((m, i) => (
                <AngleEdgePair
                    key={`${m.edgeIdx1}-${m.edgeIdx2}-${i}`}
                    m={m}
                    color={ANGLE_COLORS[i % ANGLE_COLORS.length]}
                />
            ))}
        </group>
    );
};

/* ── ThicknessTool (perpendicular distance between parallel faces) ── */

type ThicknessMeasurement = {
    face1: { point: THREE.Vector3; normal: THREE.Vector3 };
    face2: { point: THREE.Vector3; normal: THREE.Vector3 };
    value: number;
};

const THICK_COLORS = ['#cc44ff', '#44cc88', '#ff6644', '#4488ff', '#cccc33', '#ff44aa', '#33cccc', '#ff9933'];

const ThicknessTool: FC<{
    active: boolean;
    measurements: ThicknessMeasurement[];
    fmt: (mm: number) => string;
}> = ({ active, measurements, fmt }) => {
    if (!active && measurements.length === 0) return null;
    return (
        <group>
            {measurements.map((m, i) => {
                const color = THICK_COLORS[i % THICK_COLORS.length];
                return (
                    <group key={i}>
                        <mesh position={m.face1.point} raycast={() => {}}>
                            <sphereBufferGeometry args={[0.5, 16, 16]} />
                            <meshStandardMaterial color={color} />
                        </mesh>
                        <mesh position={m.face2.point} raycast={() => {}}>
                            <sphereBufferGeometry args={[0.5, 16, 16]} />
                            <meshStandardMaterial color={color} />
                        </mesh>
                        <FatLine points={[m.face1.point, m.face2.point]} color={color} />
                        <Html
                            position={[
                                (m.face1.point.x + m.face2.point.x) / 2,
                                (m.face1.point.y + m.face2.point.y) / 2 + 1.5,
                                (m.face1.point.z + m.face2.point.z) / 2,
                            ]}
                        >
                            <span style={{ ...labelStyle, borderLeft: `3px solid ${color}` }}>{fmt(m.value)}</span>
                        </Html>
                    </group>
                );
            })}
        </group>
    );
};

/* ── AreaTool (face area from clicked triangle neighborhood, multiple) ── */

type AreaMeasurement = {
    point: THREE.Vector3;
    normal: THREE.Vector3;
    value: number;
    highlight: THREE.BufferGeometry;
    planeD: number;
};

const AREA_COLORS = ['#44bbff', '#ff66aa', '#33cc66', '#ff9933', '#cc33ff', '#33cccc', '#ffcc33', '#ff3333'];

const AreaTool: FC<{
    active: boolean;
    measurements: AreaMeasurement[];
    fmtArea: (mm2: number) => string;
}> = ({ active, measurements, fmtArea }) => {
    if (!active && measurements.length === 0) return null;
    return (
        <group>
            {measurements.map((m, i) => {
                const color = AREA_COLORS[i % AREA_COLORS.length];
                const colorNum = parseInt(color.replace('#', ''), 16);
                return (
                    <group key={i}>
                        <mesh position={m.point} raycast={() => {}}>
                            <sphereBufferGeometry args={[0.5, 16, 16]} />
                            <meshStandardMaterial color={color} />
                        </mesh>
                        <arrowHelper args={[m.normal, m.point, 2, colorNum, 0.6, 0.3]} />
                        <Html position={[m.point.x, m.point.y + 1.5, m.point.z]}>
                            <span style={{ ...labelStyle, borderLeft: `3px solid ${color}` }}>{fmtArea(m.value)}</span>
                        </Html>
                    </group>
                );
            })}
        </group>
    );
};

/* ── EdgeLengthTool (click near an edge to measure its length) ──── */

type EdgeMeasurement = {
    edgeIdx: number;
    groupedEdgeIndices: number[];
    length: number;
    highlight: THREE.BufferGeometry;
    labelPos: THREE.Vector3;
    radius: number | null;
    diameter: number | null;
};

type EdgeDistMeasurement = {
    edgeIdx1: number;
    edgeIdx2: number;
    dist: number;
    pointA: THREE.Vector3;
    pointB: THREE.Vector3;
    highlight1: THREE.BufferGeometry;
    highlight2: THREE.BufferGeometry;
};

function pointToSegmentDistSq(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
    const ab = new THREE.Vector3().subVectors(b, a);
    const ap = new THREE.Vector3().subVectors(p, a);
    const t = Math.max(0, Math.min(1, ap.dot(ab) / ab.dot(ab)));
    const proj = new THREE.Vector3().copy(a).addScaledVector(ab, t);
    return p.distanceToSquared(proj);
}

function findNearestEdge(
    clientX: number,
    clientY: number,
    canvasRect: DOMRect | null,
    camera: THREE.Camera | null,
    partWorldMatrix: THREE.Matrix4 | null,
    edgeGeometries: THREE.BufferGeometry[],
    edgeBodyIdx: (number | -1)[],
    bodies: BodyState[]
): number {
    if (!canvasRect || !camera || !partWorldMatrix) return -1;
    const ndcX = ((clientX - canvasRect.left) / canvasRect.width) * 2 - 1;
    const ndcY = -((clientY - canvasRect.top) / canvasRect.height) * 2 + 1;

    let bestIdx = -1;
    let bestDistSq = Infinity;
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    edgeGeometries.forEach((geo, i) => {
        const bi = edgeBodyIdx[i];
        if (bi >= 0 && bodies[bi] && !bodies[bi].visible) return;
        const pos = geo.attributes.position;
        if (!pos || pos.count < 2) return;
        for (let s = 0; s < pos.count - 1; s++) {
            a.set(pos.getX(s), pos.getY(s), pos.getZ(s)).applyMatrix4(partWorldMatrix).project(camera);
            b.set(pos.getX(s + 1), pos.getY(s + 1), pos.getZ(s + 1))
                .applyMatrix4(partWorldMatrix)
                .project(camera);
            const ax = a.x,
                ay = a.y,
                bx = b.x,
                by = b.y;
            const dx = bx - ax,
                dy = by - ay;
            const lenSq = dx * dx + dy * dy;
            let t = lenSq > 1e-12 ? ((ndcX - ax) * dx + (ndcY - ay) * dy) / lenSq : 0;
            t = Math.max(0, Math.min(1, t));
            const px = ax + t * dx;
            const py = ay + t * dy;
            const distSq = (ndcX - px) ** 2 + (ndcY - py) ** 2;
            if (distSq < bestDistSq) {
                bestDistSq = distSq;
                bestIdx = i;
            }
        }
    });
    return bestIdx;
}

function edgeTotalLength(geo: THREE.BufferGeometry): number {
    const pos = geo.attributes.position;
    if (!pos || pos.count < 2) return 0;
    let total = 0;
    const a = new THREE.Vector3(),
        b = new THREE.Vector3();
    for (let i = 0; i < pos.count - 1; i++) {
        a.set(pos.getX(i), pos.getY(i), pos.getZ(i));
        b.set(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1));
        total += a.distanceTo(b);
    }
    return total;
}

function edgeMidpoint(geo: THREE.BufferGeometry): THREE.Vector3 {
    const pos = geo.attributes.position;
    const mid = new THREE.Vector3();
    if (!pos || pos.count === 0) return mid;
    for (let i = 0; i < pos.count; i++) {
        mid.x += pos.getX(i);
        mid.y += pos.getY(i);
        mid.z += pos.getZ(i);
    }
    mid.divideScalar(pos.count);
    return mid;
}

/** Unit direction of the first segment of an edge polyline (mesh-local space). */
function edgeFirstSegmentDirection(geo: THREE.BufferGeometry): THREE.Vector3 | null {
    const pos = geo.attributes.position;
    if (!pos || pos.count < 2) return null;
    const a = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
    const b = new THREE.Vector3(pos.getX(1), pos.getY(1), pos.getZ(1));
    const d = new THREE.Vector3().subVectors(b, a);
    const len = d.length();
    if (len < 1e-12) return null;
    return d.divideScalar(len);
}

/** Vertex on `geo` farthest from `C` (the “other end” of the edge from a corner). */
function edgeEndpoints(geo: THREE.BufferGeometry): [THREE.Vector3, THREE.Vector3] | null {
    const pos = geo.attributes.position;
    if (!pos || pos.count < 2) return null;
    const first = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
    const last = new THREE.Vector3(pos.getX(pos.count - 1), pos.getY(pos.count - 1), pos.getZ(pos.count - 1));
    return [first, last];
}

/** Furthest distance from C along +unitDir to any vertex on the edge (arc radius touches real geometry). */
function maxReachAlongRayOnEdge(C: THREE.Vector3, unitDir: THREE.Vector3, geo: THREE.BufferGeometry): number {
    const pos = geo.attributes.position;
    if (!pos || pos.count < 1) return 0;
    const d = unitDir.clone().normalize();
    const p = new THREE.Vector3();
    let best = 0;
    for (let i = 0; i < pos.count; i++) {
        p.set(pos.getX(i), pos.getY(i), pos.getZ(i));
        const proj = p.x - C.x,
            py = p.y - C.y,
            pz = p.z - C.z;
        const t = proj * d.x + py * d.y + pz * d.z;
        if (t > best) best = t;
    }
    return Math.max(0, best);
}

/** Closest pair of positions between two edge polylines (mesh-local space). */
function closestVertexPairBetweenEdges(
    geo1: THREE.BufferGeometry,
    geo2: THREE.BufferGeometry
): { p1: THREE.Vector3; p2: THREE.Vector3; distSq: number } | null {
    const a1 = geo1.attributes.position;
    const a2 = geo2.attributes.position;
    if (!a1 || !a2 || a1.count < 1 || a2.count < 1) return null;
    const u = new THREE.Vector3(),
        v = new THREE.Vector3();
    let bestD = Infinity;
    let bp1: THREE.Vector3 | null = null;
    let bp2: THREE.Vector3 | null = null;
    for (let i = 0; i < a1.count; i++) {
        u.set(a1.getX(i), a1.getY(i), a1.getZ(i));
        for (let j = 0; j < a2.count; j++) {
            v.set(a2.getX(j), a2.getY(j), a2.getZ(j));
            const d = u.distanceToSquared(v);
            if (d < bestD) {
                bestD = d;
                bp1 = u.clone();
                bp2 = v.clone();
            }
        }
    }
    if (!bp1 || !bp2) return null;
    return { p1: bp1, p2: bp2, distSq: bestD };
}

/**
 * Angle between two edges using a relative-distance corner detection.
 * If the closest vertex pair is within 30% of the shorter edge length,
 * treat it as a shared corner and measure 0°–180° from it.
 * Otherwise fall back to undirected line angle (0°–90°).
 */
function computeEdgePairAngle(
    geo1: THREE.BufferGeometry,
    geo2: THREE.BufferGeometry,
    _eps: number
): {
    angle: number;
    dir1: THREE.Vector3;
    dir2: THREE.Vector3;
    mid1: THREE.Vector3;
    mid2: THREE.Vector3;
    corner: THREE.Vector3 | null;
} | null {
    const mid1 = edgeMidpoint(geo1);
    const mid2 = edgeMidpoint(geo2);

    const cp = closestVertexPairBetweenEdges(geo1, geo2);
    if (cp) {
        const len1 = edgeTotalLength(geo1);
        const len2 = edgeTotalLength(geo2);
        const minLen = Math.min(len1, len2);
        const cpDist = Math.sqrt(cp.distSq);

        if (minLen > 1e-8 && cpDist < minLen * 0.3) {
            const C = new THREE.Vector3().addVectors(cp.p1, cp.p2).multiplyScalar(0.5);
            const ep1 = edgeEndpoints(geo1);
            const ep2 = edgeEndpoints(geo2);
            if (ep1 && ep2) {
                const [near1, far1] =
                    ep1[0].distanceToSquared(C) <= ep1[1].distanceToSquared(C) ? [ep1[0], ep1[1]] : [ep1[1], ep1[0]];
                const [near2, far2] =
                    ep2[0].distanceToSquared(C) <= ep2[1].distanceToSquared(C) ? [ep2[0], ep2[1]] : [ep2[1], ep2[0]];
                const d1 = new THREE.Vector3().subVectors(far1, near1);
                const d2 = new THREE.Vector3().subVectors(far2, near2);
                if (d1.lengthSq() > 1e-12 && d2.lengthSq() > 1e-12) {
                    d1.normalize();
                    d2.normalize();
                    const dot = Math.min(1, Math.max(-1, d1.dot(d2)));
                    const angle = Math.acos(dot) * (180 / Math.PI);
                    return { angle, dir1: d1, dir2: d2, mid1, mid2, corner: C };
                }
            }
        }
    }

    const f1 = edgeFirstSegmentDirection(geo1);
    const f2 = edgeFirstSegmentDirection(geo2);
    if (!f1 || !f2) return null;
    const absDot = Math.min(1, Math.abs(f1.dot(f2)));
    const angle = Math.acos(absDot) * (180 / Math.PI);
    const d2v = f2.clone();
    if (f1.dot(d2v) < 0) d2v.negate();
    return { angle, dir1: f1.clone(), dir2: d2v, mid1, mid2, corner: null };
}

type CircleFit = { radius: number; center: THREE.Vector3; normal: THREE.Vector3 };

function fitCircleToEdge3D(geo: THREE.BufferGeometry): CircleFit | null {
    const pos = geo.attributes.position;
    if (!pos || pos.count < 6) return null;

    const pts: THREE.Vector3[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < pos.count; i++) {
        const key = `${pos.getX(i).toFixed(6)},${pos.getY(i).toFixed(6)},${pos.getZ(i).toFixed(6)}`;
        if (!seen.has(key)) {
            seen.add(key);
            pts.push(new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)));
        }
    }
    if (pts.length < 5) return null;

    const centroid = new THREE.Vector3();
    pts.forEach(p => centroid.add(p));
    centroid.divideScalar(pts.length);

    let xx = 0,
        xy = 0,
        xz = 0,
        yy = 0,
        yz = 0,
        zz = 0;
    pts.forEach(p => {
        const dx = p.x - centroid.x,
            dy = p.y - centroid.y,
            dz = p.z - centroid.z;
        xx += dx * dx;
        xy += dx * dy;
        xz += dx * dz;
        yy += dy * dy;
        yz += dy * dz;
        zz += dz * dz;
    });

    const det_x = yy * zz - yz * yz;
    const det_y = xx * zz - xz * xz;
    const det_z = xx * yy - xy * xy;
    const maxDet = Math.max(Math.abs(det_x), Math.abs(det_y), Math.abs(det_z));
    if (maxDet < 1e-14) return null;

    let normal: THREE.Vector3;
    if (Math.abs(det_x) === maxDet) normal = new THREE.Vector3(det_x, xz * yz - xy * zz, xy * yz - xz * yy).normalize();
    else if (Math.abs(det_y) === maxDet)
        normal = new THREE.Vector3(xz * yz - xy * zz, det_y, xy * xz - yz * xx).normalize();
    else normal = new THREE.Vector3(xy * yz - xz * yy, xy * xz - yz * xx, det_z).normalize();

    const u = new THREE.Vector3();
    const minAxis =
        Math.abs(normal.x) < Math.abs(normal.y)
            ? Math.abs(normal.x) < Math.abs(normal.z)
                ? new THREE.Vector3(1, 0, 0)
                : new THREE.Vector3(0, 0, 1)
            : Math.abs(normal.y) < Math.abs(normal.z)
            ? new THREE.Vector3(0, 1, 0)
            : new THREE.Vector3(0, 0, 1);
    u.crossVectors(normal, minAxis).normalize();
    const v = new THREE.Vector3().crossVectors(normal, u).normalize();

    const pts2d = pts.map(p => {
        const d = new THREE.Vector3().subVectors(p, centroid);
        return { x: d.dot(u), y: d.dot(v) };
    });

    let su = 0,
        sv = 0,
        suu = 0,
        svv = 0,
        suuu = 0,
        svvv = 0,
        suv = 0,
        suuv = 0,
        suvv = 0;
    pts2d.forEach(p => {
        su += p.x;
        sv += p.y;
        suu += p.x * p.x;
        svv += p.y * p.y;
        suuu += p.x * p.x * p.x;
        svvv += p.y * p.y * p.y;
        suv += p.x * p.y;
        suuv += p.x * p.x * p.y;
        suvv += p.x * p.y * p.y;
    });
    const n = pts2d.length;
    const A = n * suu - su * su;
    const B = n * suv - su * sv;
    const C = n * svv - sv * sv;
    const D = 0.5 * (n * suuu + n * suvv - su * suu - su * svv);
    const E = 0.5 * (n * svvv + n * suuv - sv * suu - sv * svv);
    const denom = A * C - B * B;
    if (Math.abs(denom) < 1e-14) return null;

    const uc = (D * C - E * B) / denom;
    const vc = (A * E - B * D) / denom;

    let sumR = 0;
    let maxErr = 0;
    pts2d.forEach(p => {
        const r = Math.sqrt((p.x - uc) * (p.x - uc) + (p.y - vc) * (p.y - vc));
        sumR += r;
    });
    const avgR = sumR / n;
    pts2d.forEach(p => {
        const r = Math.sqrt((p.x - uc) * (p.x - uc) + (p.y - vc) * (p.y - vc));
        const err = Math.abs(r - avgR);
        if (err > maxErr) maxErr = err;
    });

    if (maxErr / avgR > 0.02) return null;

    const center3D = centroid.clone().addScaledVector(u, uc).addScaledVector(v, vc);

    return { radius: avgR, center: center3D, normal };
}

function findMatchingCircleEdges(
    fit: CircleFit,
    clickedIdx: number,
    allEdges: THREE.BufferGeometry[],
    edgeBodyIdx: (number | -1)[],
    bodies: BodyState[]
): number[] {
    const matched = [clickedIdx];
    allEdges.forEach((geo, i) => {
        if (i === clickedIdx) return;
        const bi = edgeBodyIdx[i];
        if (bi >= 0 && bodies[bi] && !bodies[bi].visible) return;
        const otherFit = fitCircleToEdge3D(geo);
        if (!otherFit) return;
        if (Math.abs(otherFit.radius - fit.radius) / fit.radius > 0.02) return;
        if (otherFit.center.distanceTo(fit.center) / fit.radius > 0.02) return;
        if (Math.abs(otherFit.normal.dot(fit.normal)) < 0.99) return;
        matched.push(i);
    });
    return matched;
}

function mergeEdgeGeometries(indices: number[], allEdges: THREE.BufferGeometry[]): THREE.BufferGeometry {
    const allPositions: number[] = [];
    indices.forEach(idx => {
        const pos = allEdges[idx].attributes.position;
        for (let i = 0; i < pos.count; i++) allPositions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(allPositions, 3));
    return geo;
}

function segmentToSegmentClosest(
    a1: THREE.Vector3,
    a2: THREE.Vector3,
    b1: THREE.Vector3,
    b2: THREE.Vector3
): { dist: number; pointA: THREE.Vector3; pointB: THREE.Vector3 } {
    const d1 = new THREE.Vector3().subVectors(a2, a1);
    const d2 = new THREE.Vector3().subVectors(b2, b1);
    const r = new THREE.Vector3().subVectors(a1, b1);
    const a = d1.dot(d1);
    const e = d2.dot(d2);
    const f = d2.dot(r);

    let s: number, t: number;

    if (a <= 1e-12 && e <= 1e-12) {
        s = t = 0;
    } else if (a <= 1e-12) {
        s = 0;
        t = Math.max(0, Math.min(1, f / e));
    } else {
        const c = d1.dot(r);
        if (e <= 1e-12) {
            t = 0;
            s = Math.max(0, Math.min(1, -c / a));
        } else {
            const b = d1.dot(d2);
            const denom = a * e - b * b;
            s = denom !== 0 ? Math.max(0, Math.min(1, (b * f - c * e) / denom)) : 0;
            t = (b * s + f) / e;
            if (t < 0) {
                t = 0;
                s = Math.max(0, Math.min(1, -c / a));
            } else if (t > 1) {
                t = 1;
                s = Math.max(0, Math.min(1, (b - c) / a));
            }
        }
    }

    const pointA = a1.clone().addScaledVector(d1, s);
    const pointB = b1.clone().addScaledVector(d2, t);
    return { dist: pointA.distanceTo(pointB), pointA, pointB };
}

function edgeToEdgeMinDistance(
    geoA: THREE.BufferGeometry,
    geoB: THREE.BufferGeometry
): { dist: number; pointA: THREE.Vector3; pointB: THREE.Vector3 } | null {
    const posA = geoA.attributes.position;
    const posB = geoB.attributes.position;
    if (!posA || posA.count < 2 || !posB || posB.count < 2) return null;

    let bestDist = Infinity;
    let bestPA = new THREE.Vector3();
    let bestPB = new THREE.Vector3();

    const a1 = new THREE.Vector3(),
        a2 = new THREE.Vector3();
    const b1 = new THREE.Vector3(),
        b2 = new THREE.Vector3();

    for (let i = 0; i < posA.count - 1; i++) {
        a1.set(posA.getX(i), posA.getY(i), posA.getZ(i));
        a2.set(posA.getX(i + 1), posA.getY(i + 1), posA.getZ(i + 1));
        for (let j = 0; j < posB.count - 1; j++) {
            b1.set(posB.getX(j), posB.getY(j), posB.getZ(j));
            b2.set(posB.getX(j + 1), posB.getY(j + 1), posB.getZ(j + 1));
            const result = segmentToSegmentClosest(a1, a2, b1, b2);
            if (result.dist < bestDist) {
                bestDist = result.dist;
                bestPA = result.pointA;
                bestPB = result.pointB;
            }
        }
    }

    return { dist: bestDist, pointA: bestPA, pointB: bestPB };
}

const EDGE_COLORS = ['#ffdd33', '#33ffaa', '#ff66aa', '#66ccff', '#ff8833', '#aa66ff', '#33ffdd', '#ff3366'];

const FatEdgeHighlight: FC<{ geometry: THREE.BufferGeometry; color: string }> = ({ geometry, color }) => {
    const ref = useRef<any>();
    const fatGeo = useMemo(() => {
        const pos = geometry.attributes.position;
        const arr: number[] = [];
        for (let i = 0; i < pos.count; i++) arr.push(pos.getX(i), pos.getY(i), pos.getZ(i));
        const g = new LineSegmentsGeometry();
        g.setPositions(arr);
        return g;
    }, [geometry]);

    const mat = useMemo(
        () => new LineMaterial({ color: new THREE.Color(color).getHex(), linewidth: 3, depthTest: false }),
        [color]
    );

    useFrame(() => {
        if (ref.current) ref.current.material.resolution.set(window.innerWidth, window.innerHeight);
    });

    const obj = useMemo(() => {
        const o = new LineSegments2(fatGeo, mat);
        o.raycast = () => {};
        return o;
    }, [fatGeo, mat]);
    return <primitive ref={ref} object={obj} renderOrder={999} />;
};

const EdgeLengthTool: FC<{
    active: boolean;
    measurements: EdgeMeasurement[];
    fmt: (mm: number) => string;
}> = ({ active, measurements, fmt }) => {
    if (!active && measurements.length === 0) return null;
    return (
        <group>
            {measurements.map((m, i) => {
                const color = EDGE_COLORS[i % EDGE_COLORS.length];
                return (
                    <group key={i}>
                        <FatEdgeHighlight geometry={m.highlight} color={color} />
                        <Html position={m.labelPos}>
                            <div style={{ ...labelStyle, borderLeft: `3px solid ${color}`, lineHeight: 1.6 }}>
                                {m.radius !== null && m.diameter !== null ? (
                                    <>
                                        <div style={{ fontSize: 11, opacity: 0.7 }}>R {fmt(m.radius)}</div>
                                        <div style={{ fontWeight: 600 }}>⌀ {fmt(m.diameter)}</div>
                                        <div style={{ fontSize: 11, opacity: 0.7 }}>P {fmt(m.length)}</div>
                                    </>
                                ) : (
                                    <div>{fmt(m.length)}</div>
                                )}
                            </div>
                        </Html>
                    </group>
                );
            })}
        </group>
    );
};

const EDGE_DIST_COLORS = ['#00e5ff', '#ff9100', '#76ff03', '#e040fb', '#ffd740', '#00b0ff'];

const EdgeDistLine: FC<{ m: EdgeDistMeasurement; color: string; fmt: (mm: number) => string }> = ({
    m,
    color,
    fmt,
}) => {
    const ref = useRef<any>();
    const mid = useMemo(() => new THREE.Vector3().addVectors(m.pointA, m.pointB).multiplyScalar(0.5), [
        m.pointA,
        m.pointB,
    ]);

    const connectorGeo = useMemo(() => {
        const g = new LineSegmentsGeometry();
        g.setPositions([m.pointA.x, m.pointA.y, m.pointA.z, m.pointB.x, m.pointB.y, m.pointB.z]);
        return g;
    }, [m.pointA, m.pointB]);

    const connectorMat = useMemo(
        () =>
            new LineMaterial({
                color: 0xffffff,
                linewidth: 2,
                depthTest: false,
                dashed: true,
                dashSize: 0.4,
                gapSize: 0.2,
            }),
        []
    );

    useFrame(() => {
        if (ref.current) ref.current.material.resolution.set(window.innerWidth, window.innerHeight);
    });

    const obj = useMemo(() => {
        const o = new LineSegments2(connectorGeo, connectorMat);
        o.computeLineDistances();
        o.raycast = () => {};
        return o;
    }, [connectorGeo, connectorMat]);

    return (
        <group>
            <FatEdgeHighlight geometry={m.highlight1} color={color} />
            <FatEdgeHighlight geometry={m.highlight2} color={color} />
            <primitive ref={ref} object={obj} renderOrder={999} />
            <Html position={mid}>
                <div style={{ ...labelStyle, borderLeft: `3px solid ${color}`, lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 600 }}>{fmt(m.dist)}</div>
                </div>
            </Html>
        </group>
    );
};

const EdgeDistTool: FC<{
    active: boolean;
    measurements: EdgeDistMeasurement[];
    pendingIdx: number | null;
    edges: THREE.BufferGeometry[];
    fmt: (mm: number) => string;
}> = ({ active, measurements, pendingIdx, edges, fmt }) => {
    if (!active && measurements.length === 0) return null;
    return (
        <group>
            {active && pendingIdx !== null && edges[pendingIdx] && (
                <FatEdgeHighlight geometry={edges[pendingIdx]} color="#00e5ff" />
            )}
            {measurements.map((m, i) => (
                <EdgeDistLine key={i} m={m} color={EDGE_DIST_COLORS[i % EDGE_DIST_COLORS.length]} fmt={fmt} />
            ))}
        </group>
    );
};

/* ── Main Viewer ─────────────────────────────────────────── */

type PartInfo = {
    name?: string;
    dimensions?: { length: string; width: string; height: string; unit: string };
    shape?: string;
    volume?: string;
} | null;

const EnhancedViewer: FC<{ url: string; partInfo?: PartInfo }> = ({ url, partInfo }) => {
    const [loading, setLoading] = useState(false);
    const [shapes, setShapes] = useState<any[]>([]);
    const [edges, setEdges] = useState<any[]>([]);
    const [bounds, setBounds] = useState<{ min: THREE.Vector3; max: THREE.Vector3 } | null>(null);
    const [bodies, setBodies] = useState<BodyState[]>([]);
    const [shapeBodyIdx, setShapeBodyIdx] = useState<(number | -1)[]>([]);
    const [edgeBodyIdx, setEdgeBodyIdx] = useState<(number | -1)[]>([]);
    const [bodyDetectionInfo, setBodyDetectionInfo] = useState<string>('');
    const partRef = useRef(null);
    /** Bumped after each successful model load so OrbitControls refits even if bounding-sphere radius matches the previous part. */
    const [modelFitEpoch, setModelFitEpoch] = useState(0);
    /**
     * If the model-load effect runs again with the same `url` (parent remount, StrictMode, or AntD Modal
     * reconciliation), skipping avoids wiping tools/camera and re-fetching the same JSON.
     */
    const modelLoadedForUrlRef = useRef<string | null>(null);

    const isMultiBody = bodies.length > 1;

    const [viewMode, setViewMode] = useState<ViewMode>('solid-edges');
    const [clip, setClip] = useState<ClipPlaneConfig>({ enabled: false, axis: 'y', value: 0 });
    const [activeTool, setActiveTool] = useState<ActiveTool>(null);
    const activeToolRef = useRef<ActiveTool>(null);
    activeToolRef.current = activeTool;
    const [useInches, setUseInches] = useState(true);
    const [extraRotation, setExtraRotation] = useState<[number, number, number]>([0, (Math.PI * 3) / 2, 0]);
    const [showGrid, setShowGrid] = useState(true);
    const [surfaceStyle, setSurfaceStyle] = useState<SurfaceStyle>('metallic');

    const fmt = (mm: number) => (useInches ? `${(mm / 25.4).toFixed(3)}"` : `${mm.toFixed(2)} mm`);
    const fmtArea = (mm2: number) => (useInches ? `${(mm2 / 645.16).toFixed(3)} in²` : `${(mm2 / 100).toFixed(2)} cm²`);

    const [measurements, setMeasurements] = useState<Measurement[]>([]);
    const [pendingPoint, setPendingPoint] = useState<THREE.Vector3 | null>(null);

    const [angleMeasurements, setAngleMeasurements] = useState<AngleMeasurement[]>([]);
    const [anglePending, setAnglePending] = useState<number | null>(null);

    const [thickMeasurements, setThickMeasurements] = useState<ThicknessMeasurement[]>([]);

    const [areaMeasurements, setAreaMeasurements] = useState<AreaMeasurement[]>([]);
    const [edgeMeasurements, setEdgeMeasurements] = useState<EdgeMeasurement[]>([]);
    const [edgeDistMeasurements, setEdgeDistMeasurements] = useState<EdgeDistMeasurement[]>([]);
    const [edgeDistPending, setEdgeDistPending] = useState<number | null>(null);
    const [hoveredEdgeIdx, setHoveredEdgeIdx] = useState<number>(-1);
    const [clipSliding, setClipSliding] = useState(false);

    const resetRef = useRef<(() => void) | null>(null);
    const orbitTargetRef = useRef<THREE.Vector3>(new THREE.Vector3());
    const orbitingRef = useRef<boolean>(false);
    const lastOrbitEndRef = useRef<number>(0);
    const cameraRef = useRef<THREE.Camera | null>(null);
    const navigateRef = useRef<((o: Orientation) => void) | null>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const r3fGlRef = useRef<THREE.WebGLRenderer | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const [rootWidth, setRootWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1000);
    const [sidebarOverlayOpen, setSidebarOverlayOpen] = useState(false);
    const [optionsPanelPos, setOptionsPanelPos] = useState({ x: 16, y: 56 });
    const optionsPanelDragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
    const wasOptionsPanelOpen = useRef(false);
    const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
    const [viewportWH, setViewportWH] = useState(() =>
        typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 1200, h: 800 }
    );
    const [optionsInPopup, setOptionsInPopup] = useState(false);
    const [optionsPortalDoc, setOptionsPortalDoc] = useState<Document | null>(null);
    const optionsPopupWinRef = useRef<Window | null>(null);
    const optionsInPopupRef = useRef(false);

    useEffect(() => {
        optionsInPopupRef.current = optionsInPopup;
    }, [optionsInPopup]);

    const closeCollapsedOptions = useCallback(() => {
        setSidebarOverlayOpen(false);
        setOptionsPortalDoc(null);
        setOptionsInPopup(false);
        const pw = optionsPopupWinRef.current;
        if (pw && !pw.closed) {
            try {
                pw.close();
            } catch {
                /* noop */
            }
        }
        optionsPopupWinRef.current = null;
    }, []);

    const openCollapsedOptions = useCallback(() => {
        let w = optionsPopupWinRef.current;
        if (w && !w.closed) {
            w.focus();
            setOptionsPortalDoc(w.document);
            setOptionsInPopup(true);
            setSidebarOverlayOpen(true);
            return;
        }
        // Do not pass `noreferrer`/`noopener` in the feature string: the spec makes the API return
        // `null` (no Window handle), which breaks document.write + portal; some browsers also leave
        // an extra blank / about:blank#blocked tab. Same-origin popup only; we need the handle.
        w = window.open(
            'about:blank',
            'pb-viewer-options',
            `width=${OPTIONS_POPUP_WIDTH},height=${OPTIONS_POPUP_HEIGHT},left=120,top=80,scrollbars=yes,resizable=yes`
        );
        if (w) {
            try {
                w.document.open();
                w.document.write(
                    '<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head><body style="margin:0;background:#232a36;overflow:auto"></body></html>'
                );
                w.document.close();
                copyStylesIntoPopup(w.document);
            } catch {
                /* ignore */
            }
            optionsPopupWinRef.current = w;
            setOptionsPortalDoc(w.document);
            setOptionsInPopup(true);
        } else {
            optionsPopupWinRef.current = null;
            setOptionsInPopup(false);
            setOptionsPortalDoc(typeof document !== 'undefined' ? document : null);
        }
        setSidebarOverlayOpen(true);
    }, []);

    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect?.width ?? 0;
            setRootWidth(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const fn = () => setViewportWH({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener('resize', fn);
        fn();
        return () => window.removeEventListener('resize', fn);
    }, []);

    useEffect(() => {
        return () => {
            const pw = optionsPopupWinRef.current;
            if (pw && !pw.closed) {
                try {
                    pw.close();
                } catch {
                    /* noop */
                }
            }
        };
    }, []);

    const layoutMode =
        rootWidth >= VIEWER_LAYOUT_NORMAL_MIN
            ? 'normal'
            : rootWidth >= VIEWER_LAYOUT_COMPACT_MIN
            ? 'compact'
            : 'collapsed';

    useEffect(() => {
        if (layoutMode !== 'collapsed') closeCollapsedOptions();
    }, [layoutMode, closeCollapsedOptions]);

    const optionsPanelWidth = optionsFloatingPanelWidth(viewportWH.w);

    useEffect(() => {
        if (layoutMode !== 'collapsed') {
            wasOptionsPanelOpen.current = false;
            return;
        }
        if (optionsInPopup) {
            wasOptionsPanelOpen.current = sidebarOverlayOpen;
            return;
        }
        const justOpened = sidebarOverlayOpen && !wasOptionsPanelOpen.current;
        wasOptionsPanelOpen.current = sidebarOverlayOpen;
        if (!justOpened || typeof window === 'undefined') return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const w = optionsFloatingPanelWidth(vw);
        const estH = Math.min(vh * 0.425, 280);
        setOptionsPanelPos({
            x: Math.max(8, (vw - w) / 2),
            y: Math.max(8, (vh - estH) / 2),
        });
    }, [layoutMode, sidebarOverlayOpen, optionsInPopup]);

    useEffect(() => {
        if (!sidebarOverlayOpen || !optionsInPopup) return;
        const id = window.setInterval(() => {
            const pw = optionsPopupWinRef.current;
            if (!pw || pw.closed) closeCollapsedOptions();
        }, 400);
        return () => window.clearInterval(id);
    }, [sidebarOverlayOpen, optionsInPopup, closeCollapsedOptions]);

    const compactSizing = layoutMode === 'compact';
    /** Collapsed overlay: one horizontal row per group, full-size labels. */
    const overlayToolsRow = layoutMode === 'collapsed' && sidebarOverlayOpen;
    /** Narrow inline sidebar: single column of full-width controls (readable). */
    const compactSingleColumn = compactSizing && !overlayToolsRow;
    // Compact sidebar width (adjusted to balance space and readability)
    const sideW = compactSizing ? 150 : 260;
    const sidePad = compactSizing ? 10 : 12;

    const showEdges = viewMode === 'solid-edges' || viewMode === 'xray';
    const clipPlanes = useMemo(() => {
        if (!clip.enabled) return [];
        if (clip.axis === 'face' && !clip.faceNormal) return [];
        return [buildClipPlane(clip, extraRotation)];
    }, [clip.enabled, clip.axis, clip.value, clip.faceNormal, extraRotation]);
    const clipIsAtInitial = bounds ? clip.value === getClipInitialValue(clip.axis, bounds, clip.faceRange) : true;

    useEffect(() => {
        const gl = r3fGlRef.current;
        if (!gl?.domElement) return;
        gl.domElement.style.cursor = activeTool === 'thickness' ? 'crosshair' : '';
    }, [activeTool]);

    const toggleTool = (tool: ActiveTool) => {
        setActiveTool(prev => (prev === tool ? null : tool));
        setPendingPoint(null);
        setEdgeDistPending(null);
        setAnglePending(null);
        setHoveredEdgeIdx(-1);
    };

    useEffect(() => {
        const el = canvasContainerRef.current;
        if (!el || !clip.enabled || !bounds) return;

        let wheelTimer: any = null;
        const handleWheel = (e: WheelEvent) => {
            if (!e.shiftKey) return;
            // prevent OrbitControls zooming when using Shift+Scroll for clipping
            e.preventDefault();
            // stop other wheel listeners (OrbitControls) from handling this event
            (e as any).stopImmediatePropagation?.();
            setClipSliding(true);
            clearTimeout(wheelTimer);
            wheelTimer = setTimeout(() => setClipSliding(false), 300);
            setClip(prev => {
                const isFace = prev.axis === 'face' && prev.faceRange;
                const minV = isFace ? prev.faceRange!.min : bounds.min[prev.axis as 'x' | 'y' | 'z'];
                const maxV = isFace ? prev.faceRange!.max : bounds.max[prev.axis as 'x' | 'y' | 'z'];
                const range = maxV - minV;
                // Normalize deltaMode (pixels/lines/pages) for consistent behavior across devices
                const modeScale =
                    (e as any).deltaMode === 1
                        ? 16
                        : (e as any).deltaMode === 2
                        ? typeof window !== 'undefined'
                            ? window.innerHeight
                            : 800
                        : 1;
                const normalized = e.deltaY * modeScale;
                // sensitivity tuned to move reasonably with both wheel and trackpad
                const sensitivity = 0.0025;
                const deltaValue = normalized * range * sensitivity;
                // Invert sign so wheel down (positive deltaY) decreases clip value
                return { ...prev, value: Math.max(minV, Math.min(maxV, prev.value + deltaValue)) };
            });
        };

        // Use capture so we intercept the wheel before OrbitControls (which also listens)
        el.addEventListener('wheel', handleWheel, { passive: false, capture: true });
        return () => el.removeEventListener('wheel', handleWheel, { capture: true });
    }, [clip.enabled, clip.axis, bounds]);

    const handleReset = () => {
        setViewMode('solid-edges');
        setClip({ enabled: false, axis: 'y', value: 0 });
        setExtraRotation([0, (Math.PI * 3) / 2, 0]);
        setActiveTool(null);
        setPendingPoint(null);
        setAnglePending(null);
        setHoveredEdgeIdx(-1);
        setBodies(prev => prev.map(b => ({ ...b, visible: true })));
        setShowGrid(true);
        if (resetRef.current) resetRef.current();
    };

    // Escape key: clear all measurements and reset view
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === 'Esc') {
                // Clear measurements
                setMeasurements([]);
                setPendingPoint(null);
                setAngleMeasurements([]);
                setAnglePending(null);
                setThickMeasurements([]);
                setAreaMeasurements([]);
                setEdgeMeasurements([]);
                setEdgeDistMeasurements([]);
                setEdgeDistPending(null);
                setHoveredEdgeIdx(-1);
                setActiveTool(null);
                // Reset view/camera and other UI
                handleReset();
            }
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [handleReset]);

    const wasDragRef = useRef(false);

    useEffect(() => {
        const el = canvasContainerRef.current;
        if (!el) return;
        let startX = 0,
            startY = 0;
        const onDown = (e: PointerEvent) => {
            startX = e.clientX;
            startY = e.clientY;
            wasDragRef.current = false;
        };
        const onUp = (e: PointerEvent) => {
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            wasDragRef.current = dx * dx + dy * dy > 16;
        };
        el.addEventListener('pointerdown', onDown, true);
        el.addEventListener('pointerup', onUp, true);
        return () => {
            el.removeEventListener('pointerdown', onDown, true);
            el.removeEventListener('pointerup', onUp, true);
        };
    }, []);

    const handlePointerDown = () => {};
    const isDrag = () => wasDragRef.current;

    const handleDoubleClick = (e: any) => {
        e.stopPropagation();
        if (!clip.enabled || clip.axis !== 'face' || !bounds) return;
        const fn = e.face ? new THREE.Vector3().copy(e.face.normal).normalize() : null;
        if (!fn) return;
        const range = projectBoundsOnNormal(bounds, fn);
        setClip(prev => ({ ...prev, faceNormal: fn.clone(), faceRange: range, value: range.max }));
    };

    const handleClick = (e: any) => {
        e.stopPropagation();

        // Face clip selection: always respond to a click (bypass drag detection)
        if (!activeTool && clip.enabled && clip.axis === 'face' && e.face && bounds) {
            const localFn = new THREE.Vector3().copy(e.face.normal).normalize();
            const range = projectBoundsOnNormal(bounds, localFn);
            setClip(prev => ({ ...prev, faceNormal: localFn.clone(), faceRange: range, value: range.max }));
            return;
        }

        // If the pointer moved between down and up, the user was orbiting/panning — not clicking.
        if (isDrag()) return;
        if (e.point == null) return;

        const pt = new THREE.Vector3(e.point.x, e.point.y, e.point.z);
        const faceNormal =
            e.face && e.object
                ? new THREE.Vector3().copy(e.face.normal).transformDirection(e.object.matrixWorld).normalize()
                : null;

        if (activeTool) {
            if (activeTool === 'ruler') {
                if (pendingPoint) {
                    const line = new THREE.BufferGeometry().setFromPoints([pendingPoint, pt]);
                    const mid = new THREE.Vector3();
                    new THREE.Line3(pendingPoint, pt).getCenter(mid);
                    setMeasurements(prev => [
                        ...prev,
                        { p1: pendingPoint, p2: pt, line, mid, dist: pendingPoint.distanceTo(pt) },
                    ]);
                    setPendingPoint(null);
                } else {
                    setPendingPoint(pt);
                }
            }

            if (activeTool === 'angle') {
                const cx = e.nativeEvent?.clientX ?? e.clientX;
                const cy = e.nativeEvent?.clientY ?? e.clientY;
                const rect = canvasContainerRef.current?.getBoundingClientRect() ?? null;
                const partMat = partRef.current ? partRef.current.matrixWorld : null;
                const idx = findNearestEdge(cx, cy, rect, cameraRef.current, partMat, edges, edgeBodyIdx, bodies);
                if (idx >= 0) {
                    const inMeasurement = angleMeasurements.find(m => m.edgeIdx1 === idx || m.edgeIdx2 === idx);
                    if (inMeasurement) {
                        setAngleMeasurements(prev => prev.filter(m => m !== inMeasurement));
                    } else if (anglePending === null) {
                        setAnglePending(idx);
                    } else if (anglePending !== idx) {
                        const diag = bounds ? bounds.min.distanceTo(bounds.max) : 50;
                        const eps = Math.max(1.0, diag * 0.005);
                        const computed = computeEdgePairAngle(edges[anglePending], edges[idx], eps);
                        if (computed) {
                            setAngleMeasurements(prev => [
                                ...prev,
                                {
                                    edgeIdx1: anglePending,
                                    edgeIdx2: idx,
                                    dir1: computed.dir1,
                                    dir2: computed.dir2,
                                    mid1: computed.mid1,
                                    mid2: computed.mid2,
                                    corner: computed.corner,
                                    angle: computed.angle,
                                    highlight1: edges[anglePending],
                                    highlight2: edges[idx],
                                },
                            ]);
                        }
                        setAnglePending(null);
                    }
                }
            }

            if (activeTool === 'thickness') {
                if (!faceNormal) {
                    pointerDownPos.current = null;
                    return;
                }
                const meshes: THREE.Mesh[] = [];
                partRef.current?.traverse((c: any) => {
                    if ((c as any).isMesh && (c as any).material && (c as any).material.colorWrite !== false)
                        meshes.push(c as THREE.Mesh);
                });
                const savedSides = meshes.map(m => (m.material as THREE.Material).side);
                meshes.forEach(m => {
                    (m.material as THREE.Material).side = THREE.DoubleSide;
                });
                const rc = new THREE.Raycaster();
                const origin = pt.clone().addScaledVector(faceNormal, 0.05);
                rc.set(origin, faceNormal.clone().negate());
                const hits = rc.intersectObjects(meshes, false);
                meshes.forEach((m, i) => {
                    (m.material as THREE.Material).side = savedSides[i];
                });
                const opp = hits.find(h => h.point.distanceTo(pt) > 0.05);
                if (opp) {
                    const n2 = opp.face
                        ? new THREE.Vector3()
                              .copy(opp.face.normal)
                              .transformDirection(opp.object.matrixWorld)
                              .normalize()
                        : faceNormal.clone().negate();
                    setThickMeasurements(prev => [
                        ...prev,
                        {
                            face1: { point: pt, normal: faceNormal },
                            face2: { point: opp.point, normal: n2 },
                            value: pt.distanceTo(opp.point),
                        },
                    ]);
                }
            }

            if (activeTool === 'edge') {
                const cx = e.nativeEvent?.clientX ?? e.clientX;
                const cy = e.nativeEvent?.clientY ?? e.clientY;
                const rect = canvasContainerRef.current?.getBoundingClientRect() ?? null;
                const partMat = partRef.current ? partRef.current.matrixWorld : null;
                const idx = findNearestEdge(cx, cy, rect, cameraRef.current, partMat, edges, edgeBodyIdx, bodies);
                if (idx >= 0) {
                    const alreadyInGroup = edgeMeasurements.find(m => m.groupedEdgeIndices.includes(idx));
                    if (alreadyInGroup) {
                        setEdgeMeasurements(prev => prev.filter(m => !m.groupedEdgeIndices.includes(idx)));
                    } else {
                        const fit = fitCircleToEdge3D(edges[idx]);
                        if (fit) {
                            const group = findMatchingCircleEdges(fit, idx, edges, edgeBodyIdx, bodies);
                            const totalLength = group.reduce((sum, gi) => sum + edgeTotalLength(edges[gi]), 0);
                            const highlight = mergeEdgeGeometries(group, edges);
                            const labelPos = edgeMidpoint(edges[idx]);
                            setEdgeMeasurements(prev => [
                                ...prev,
                                {
                                    edgeIdx: idx,
                                    groupedEdgeIndices: group,
                                    length: totalLength,
                                    highlight,
                                    labelPos,
                                    radius: fit.radius,
                                    diameter: fit.radius * 2,
                                },
                            ]);
                        } else {
                            const geo = edges[idx];
                            const length = edgeTotalLength(geo);
                            const labelPos = edgeMidpoint(geo);
                            setEdgeMeasurements(prev => [
                                ...prev,
                                {
                                    edgeIdx: idx,
                                    groupedEdgeIndices: [idx],
                                    length,
                                    highlight: geo,
                                    labelPos,
                                    radius: null,
                                    diameter: null,
                                },
                            ]);
                        }
                    }
                }
            }

            if (activeTool === 'edgeDist') {
                const cx = e.nativeEvent?.clientX ?? e.clientX;
                const cy = e.nativeEvent?.clientY ?? e.clientY;
                const rect = canvasContainerRef.current?.getBoundingClientRect() ?? null;
                const partMat = partRef.current ? partRef.current.matrixWorld : null;
                const idx = findNearestEdge(cx, cy, rect, cameraRef.current, partMat, edges, edgeBodyIdx, bodies);
                if (idx >= 0) {
                    const alreadyUsed = edgeDistMeasurements.find(m => m.edgeIdx1 === idx || m.edgeIdx2 === idx);
                    if (alreadyUsed) {
                        setEdgeDistMeasurements(prev => prev.filter(m => m.edgeIdx1 !== idx && m.edgeIdx2 !== idx));
                    } else if (edgeDistPending === null) {
                        setEdgeDistPending(idx);
                    } else if (edgeDistPending !== idx) {
                        const result = edgeToEdgeMinDistance(edges[edgeDistPending], edges[idx]);
                        if (result) {
                            setEdgeDistMeasurements(prev => [
                                ...prev,
                                {
                                    edgeIdx1: edgeDistPending,
                                    edgeIdx2: idx,
                                    dist: result.dist,
                                    pointA: result.pointA,
                                    pointB: result.pointB,
                                    highlight1: edges[edgeDistPending],
                                    highlight2: edges[idx],
                                },
                            ]);
                        }
                        setEdgeDistPending(null);
                    }
                }
            }

            if (activeTool === 'area' && e.object && e.faceIndex !== undefined && e.face) {
                const geo = e.object.geometry as THREE.BufferGeometry;
                const localNormal = e.face ? new THREE.Vector3().copy(e.face.normal).normalize() : null;
                if (geo && localNormal) {
                    const pos = geo.attributes.position;
                    const idx = geo.index;
                    const normalThreshold = 0.99;

                    const ca = new THREE.Vector3().fromBufferAttribute(pos, e.face.a);
                    const cb = new THREE.Vector3().fromBufferAttribute(pos, e.face.b);
                    const cc = new THREE.Vector3().fromBufferAttribute(pos, e.face.c);
                    const clickedCenter = new THREE.Vector3().add(ca).add(cb).add(cc).divideScalar(3);
                    const clickedPlaneD = localNormal.dot(clickedCenter);

                    const existing = areaMeasurements.findIndex(
                        m =>
                            Math.abs(m.normal.dot(localNormal)) > normalThreshold &&
                            Math.abs(m.planeD - clickedPlaneD) < 0.5
                    );
                    if (existing >= 0) {
                        setAreaMeasurements(prev => prev.filter((_, i) => i !== existing));
                    } else {
                        const triCount = idx ? idx.count / 3 : pos.count / 3;
                        const a = new THREE.Vector3(),
                            b = new THREE.Vector3(),
                            c = new THREE.Vector3();
                        const triNormal = new THREE.Vector3();
                        const ab = new THREE.Vector3(),
                            ac = new THREE.Vector3();
                        const matchingVerts: number[] = [];
                        let totalArea = 0;

                        for (let i = 0; i < triCount; i++) {
                            const i0 = idx ? idx.getX(i * 3) : i * 3;
                            const i1 = idx ? idx.getX(i * 3 + 1) : i * 3 + 1;
                            const i2 = idx ? idx.getX(i * 3 + 2) : i * 3 + 2;
                            a.fromBufferAttribute(pos, i0);
                            b.fromBufferAttribute(pos, i1);
                            c.fromBufferAttribute(pos, i2);
                            ab.subVectors(b, a);
                            ac.subVectors(c, a);
                            triNormal.crossVectors(ab, ac);
                            const len = triNormal.length();
                            if (len === 0) continue;
                            triNormal.divideScalar(len);

                            if (Math.abs(triNormal.dot(localNormal)) < normalThreshold) continue;

                            const triCenter = new THREE.Vector3().addVectors(a, b).add(c).divideScalar(3);
                            const triPlaneD = localNormal.dot(triCenter);
                            const maxDim = Math.max(
                                geo.boundingBox ? geo.boundingBox.max.x - geo.boundingBox.min.x : 100,
                                geo.boundingBox ? geo.boundingBox.max.y - geo.boundingBox.min.y : 100,
                                geo.boundingBox ? geo.boundingBox.max.z - geo.boundingBox.min.z : 100
                            );
                            if (Math.abs(triPlaneD - clickedPlaneD) > maxDim * 0.01) continue;

                            totalArea += len * 0.5;
                            matchingVerts.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
                        }

                        if (matchingVerts.length > 0) {
                            const hlGeo = new THREE.BufferGeometry();
                            hlGeo.setAttribute('position', new THREE.Float32BufferAttribute(matchingVerts, 3));
                            setAreaMeasurements(prev => [
                                ...prev,
                                {
                                    point: pt,
                                    normal: localNormal,
                                    value: totalArea,
                                    highlight: hlGeo,
                                    planeD: clickedPlaneD,
                                },
                            ]);
                        }
                    }
                }
            }

            pointerDownPos.current = null;
            return;
        }

        pointerDownPos.current = null;
    };

    const handlePointerMove = (e: any) => {
        if (activeTool !== 'edge' && activeTool !== 'edgeDist' && activeTool !== 'angle') {
            if (hoveredEdgeIdx !== -1) setHoveredEdgeIdx(-1);
            return;
        }
        e.stopPropagation();
        const cx = e.nativeEvent?.clientX ?? e.clientX;
        const cy = e.nativeEvent?.clientY ?? e.clientY;
        const rect = canvasContainerRef.current?.getBoundingClientRect() ?? null;
        const partMat = partRef.current ? partRef.current.matrixWorld : null;
        const idx = findNearestEdge(cx, cy, rect, cameraRef.current, partMat, edges, edgeBodyIdx, bodies);
        if (idx !== hoveredEdgeIdx) setHoveredEdgeIdx(idx);
    };

    const handlePointerLeave = () => {
        setHoveredEdgeIdx(-1);
    };

    useEffect(() => {
        if (!url) {
            modelLoadedForUrlRef.current = null;
            return;
        }
        if (modelLoadedForUrlRef.current === url) {
            return;
        }

        setMeasurements([]);
        setPendingPoint(null);
        setAngleMeasurements([]);
        setAnglePending(null);
        setThickMeasurements([]);
        setAreaMeasurements([]);
        setEdgeMeasurements([]);
        setEdgeDistMeasurements([]);
        setEdgeDistPending(null);
        setHoveredEdgeIdx(-1);
        setActiveTool(null);
        setClipSliding(false);
        setViewMode('solid-edges');
        setClip({ enabled: false, axis: 'y', value: 0, faceNormal: undefined, faceRange: undefined });
        setExtraRotation([0, (Math.PI * 3) / 2, 0]);
        setShowGrid(true);

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const loader = new THREE.BufferGeometryLoader();
                const json = await fetchJSON<any>(url);
                if (cancelled) return;
                const model = json.model;

                const shapeKeys = Object.keys(model.shapes);
                const edgeKeys = Object.keys(model.edges);
                const s: any[] = [],
                    ed: any[] = [];
                const gMin = new THREE.Vector3(Infinity, Infinity, Infinity);
                const gMax = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

                shapeKeys.forEach(id => {
                    const bg = loader.parse(model.shapes[id]);
                    bg.computeBoundingBox();
                    bg.computeBoundingSphere();
                    gMin.min(bg.boundingBox.min);
                    gMax.max(bg.boundingBox.max);
                    s.push(bg);
                });
                edgeKeys.forEach(id => {
                    ed.push(loader.parse(model.edges[id]));
                });

                const bodyDefs: BodyDef[] | undefined = model.bodies;
                if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
                    console.log(
                        '[3D Viewer] Shapes:',
                        shapeKeys.length,
                        '| Edges:',
                        edgeKeys.length,
                        '| Bodies in JSON:',
                        bodyDefs ? bodyDefs.length : 'none'
                    );
                }

                const FALLBACK_COLORS = [
                    '#a3c4f3',
                    '#f6b89a',
                    '#a3f0b3',
                    '#d4a3f0',
                    '#f0e68c',
                    '#a3f0e0',
                    '#f0a3b3',
                    '#d2d2f6',
                ];

                if (bodyDefs && bodyDefs.length >= 1) {
                    const bodyStates: BodyState[] = bodyDefs.map(b => ({
                        id: b.id,
                        label: b.label,
                        color: rgbToHex(b.color[0], b.color[1], b.color[2]),
                        visible: true,
                        shapeIndices: b.shape_ids.map(sid => shapeKeys.indexOf(sid)).filter(idx => idx >= 0),
                        edgeIndices: b.edge_ids.map(eid => edgeKeys.indexOf(eid)).filter(idx => idx >= 0),
                    }));
                    setBodies(bodyStates);
                    setBodyDetectionInfo(`${bodyDefs.length} ${bodyDefs.length === 1 ? 'body' : 'bodies'} detected`);

                    const sMap = new Array(s.length).fill(-1);
                    const eMap = new Array(ed.length).fill(-1);
                    bodyStates.forEach((b, bi) => {
                        b.shapeIndices.forEach(si => {
                            sMap[si] = bi;
                        });
                        b.edgeIndices.forEach(ei => {
                            eMap[ei] = bi;
                        });
                    });
                    setShapeBodyIdx(sMap);
                    setEdgeBodyIdx(eMap);
                } else if (shapeKeys.length > 1) {
                    const bodyStates: BodyState[] = shapeKeys.map((sk, i) => ({
                        id: `shape_${i}`,
                        label: `Body ${i + 1}`,
                        color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
                        visible: true,
                        shapeIndices: [i],
                        edgeIndices: [],
                    }));
                    setBodies(bodyStates);
                    setBodyDetectionInfo(`${shapeKeys.length} bodies (detected from shapes)`);

                    const sMap = shapeKeys.map((_, i) => i);
                    setShapeBodyIdx(sMap);
                    setEdgeBodyIdx(new Array(ed.length).fill(-1));
                } else {
                    setBodies([]);
                    setShapeBodyIdx([]);
                    setEdgeBodyIdx([]);
                    setBodyDetectionInfo('Single body');
                }

                setShapes(s);
                setEdges(ed);
                setBounds({ min: gMin, max: gMax });
                setClip(prev => ({ ...prev, value: getClipInitialValue(prev.axis, { min: gMin, max: gMax }) }));
                setModelFitEpoch(e => e + 1);
                if (!cancelled) modelLoadedForUrlRef.current = url;
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [url]);

    const slMin =
        clip.axis === 'face' && clip.faceRange
            ? clip.faceRange.min
            : bounds && clip.axis !== 'face'
            ? bounds.min[clip.axis]
            : 0;
    const slMax =
        clip.axis === 'face' && clip.faceRange
            ? clip.faceRange.max
            : bounds && clip.axis !== 'face'
            ? bounds.max[clip.axis]
            : 100;
    const slStep = (slMax - slMin) / 200 || 0.1;

    const onOptionsPanelMove = (e: MouseEvent) => {
        const d = optionsPanelDragRef.current;
        if (!d) return;
        let nx = d.ox + (e.clientX - d.sx);
        let ny = d.oy + (e.clientY - d.sy);
        if (!optionsInPopupRef.current && typeof window !== 'undefined') {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const pw = optionsFloatingPanelWidth(vw);
            nx = Math.max(0, Math.min(nx, Math.max(0, vw - pw)));
            ny = Math.max(0, Math.min(ny, Math.max(0, vh - 36)));
        }
        setOptionsPanelPos({ x: nx, y: ny });
    };

    const onOptionsPanelUp = () => {
        optionsPanelDragRef.current = null;
        if (typeof document !== 'undefined') document.body.style.cursor = '';
        window.removeEventListener('mousemove', onOptionsPanelMove);
        window.removeEventListener('mouseup', onOptionsPanelUp);
    };

    const onOptionsPanelHeaderDown = (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        optionsPanelDragRef.current = {
            sx: e.clientX,
            sy: e.clientY,
            ox: optionsPanelPos.x,
            oy: optionsPanelPos.y,
        };
        if (typeof document !== 'undefined') document.body.style.cursor = 'grabbing';
        window.addEventListener('mousemove', onOptionsPanelMove);
        window.addEventListener('mouseup', onOptionsPanelUp);
    };

    const c = compactSizing;
    const row = overlayToolsRow;
    const col = compactSingleColumn;

    const renderSidebarPanel = () => (
        <>
            {/* Part name */}
            {partInfo?.name && (
                <div style={{ marginBottom: c ? 8 : 10 }}>
                    <div
                        style={{
                            fontSize: c ? 12 : 13,
                            fontWeight: 600,
                            color: '#fff',
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical' as any,
                        }}
                    >
                        {partInfo.name}
                    </div>
                    {partInfo.shape && (
                        <div style={{ fontSize: c ? 10 : 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                            {partInfo.shape}
                        </div>
                    )}
                </div>
            )}

            {/* Dimensions */}
            {partInfo?.dimensions && (
                <div
                    style={{
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                        paddingTop: c ? 6 : 8,
                        marginBottom: c ? 6 : 8,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            gap: c ? 4 : 6,
                            fontSize: c ? 11 : 12,
                            flexWrap: c ? 'wrap' : 'nowrap',
                        }}
                    >
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>L</span>
                        <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                            {fmt(parseFloat(partInfo.dimensions.length))}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.25)' }}>|</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>W</span>
                        <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                            {fmt(parseFloat(partInfo.dimensions.width))}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.25)' }}>|</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>H</span>
                        <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                            {fmt(parseFloat(partInfo.dimensions.height))}
                        </span>
                    </div>
                    {partInfo.volume && (
                        <div style={{ marginTop: 3, fontSize: c ? 10 : 11, color: 'rgba(255,255,255,0.45)' }}>
                            Vol:{' '}
                            {useInches
                                ? (parseFloat(partInfo.volume) / 16387.064).toFixed(3) + ' in³'
                                : (parseFloat(partInfo.volume) / 1000).toFixed(2) + ' cm³'}
                        </div>
                    )}
                </div>
            )}

            {/* Bodies */}
            <div
                style={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: c ? 6 : 8,
                    marginBottom: c ? 6 : 8,
                }}
            >
                <div
                    style={{
                        fontSize: c ? 9 : 10,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.3)',
                        textTransform: 'uppercase',
                        letterSpacing: c ? 0.5 : 1,
                        marginBottom: c ? 5 : 6,
                    }}
                >
                    Bodies {bodies.length > 0 ? `(${bodies.length})` : ''}
                </div>

                {bodies.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {bodies.map((b, i) => (
                            <div
                                key={b.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '3px 6px',
                                    borderRadius: 4,
                                    background: b.visible ? 'rgba(255,255,255,0.06)' : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'background 0.15s',
                                }}
                                onClick={() => {
                                    setBodies(prev =>
                                        prev.map((bb, j) => (j === i ? { ...bb, visible: !bb.visible } : bb))
                                    );
                                }}
                                onDoubleClick={() => {
                                    setBodies(prev => {
                                        const allOthersHidden = prev.every((bb, j) =>
                                            j === i ? bb.visible : !bb.visible
                                        );
                                        if (allOthersHidden) return prev.map(bb => ({ ...bb, visible: true }));
                                        return prev.map((bb, j) => ({ ...bb, visible: j === i }));
                                    });
                                }}
                            >
                                <div
                                    style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: 3,
                                        flexShrink: 0,
                                        background: b.visible ? b.color : 'transparent',
                                        border: `2px solid ${b.color}`,
                                        opacity: b.visible ? 1 : 0.4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {b.visible && (
                                        <span style={{ color: '#000', fontSize: 9, fontWeight: 800, lineHeight: 1 }}>
                                            ✓
                                        </span>
                                    )}
                                </div>
                                <span
                                    style={{
                                        flex: 1,
                                        fontSize: 12,
                                        color: b.visible ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {b.label}
                                </span>
                            </div>
                        ))}
                        {bodies.length > 1 && (
                            <div style={{ display: 'flex', gap: 3, marginTop: 2 }}>
                                <div
                                    onClick={() => setBodies(prev => prev.map(b => ({ ...b, visible: true })))}
                                    style={{
                                        flex: 1,
                                        padding: '2px 0',
                                        borderRadius: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        fontSize: 10,
                                        color: 'rgba(255,255,255,0.5)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                    }}
                                >
                                    Show All
                                </div>
                                <div
                                    onClick={() => setBodies(prev => prev.map(b => ({ ...b, visible: false })))}
                                    style={{
                                        flex: 1,
                                        padding: '2px 0',
                                        borderRadius: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        fontSize: 10,
                                        color: 'rgba(255,255,255,0.5)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                    }}
                                >
                                    Hide All
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', lineHeight: 1.4 }}>
                        {bodyDetectionInfo || 'Loading...'}
                    </div>
                )}
            </div>

            {/* Display - compact pill buttons */}
            <div
                style={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: c ? 6 : 8,
                    marginBottom: c ? 6 : 8,
                }}
            >
                <div
                    style={{
                        fontSize: c ? 9 : 10,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.3)',
                        textTransform: 'uppercase',
                        letterSpacing: c ? 0.5 : 1,
                        marginBottom: c ? 5 : 6,
                    }}
                >
                    Display
                </div>
                <div
                    style={
                        row
                            ? { display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 3 }
                            : col
                            ? { display: 'flex', flexDirection: 'column', gap: 3 }
                            : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }
                    }
                >
                    {([
                        ['solid-edges', 'Solid + Edge', 'S+E'],
                        ['solid', 'Solid', 'Solid'],
                        ['xray', 'X-Ray', 'X-Ray'],
                    ] as [ViewMode, string, string][]).map(([v, label, short]) => (
                        <div
                            key={v}
                            onClick={() => setViewMode(v)}
                            style={{
                                padding: c ? '3px 4px' : '4px 0',
                                borderRadius: 4,
                                textAlign: 'center',
                                cursor: 'pointer',
                                fontSize: c ? 10 : 11,
                                fontWeight: 600,
                                transition: 'all 0.15s',
                                flex: row ? '1 1 0' : undefined,
                                minWidth: row ? 0 : undefined,
                                alignSelf: col ? 'stretch' : undefined,
                                background: viewMode === v ? 'rgba(24,144,255,0.85)' : 'rgba(255,255,255,0.06)',
                                color: viewMode === v ? '#fff' : 'rgba(255,255,255,0.6)',
                                border: `1px solid ${
                                    viewMode === v ? 'rgba(24,144,255,0.5)' : 'rgba(255,255,255,0.08)'
                                }`,
                            }}
                        >
                            {label}
                        </div>
                    ))}
                </div>
                <div
                    style={{
                        fontSize: c ? 9 : 10,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.3)',
                        textTransform: 'uppercase',
                        letterSpacing: c ? 0.5 : 1,
                        marginTop: c ? 6 : 8,
                        marginBottom: c ? 5 : 6,
                    }}
                >
                    Surface
                </div>
                <div
                    style={
                        row
                            ? { display: 'flex', flexDirection: 'row', gap: 3 }
                            : col
                            ? { display: 'flex', flexDirection: 'column', gap: 3 }
                            : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }
                    }
                >
                    {([
                        ['flat', 'Flat'],
                        ['metallic', 'Metal'],
                    ] as [SurfaceStyle, string][]).map(([v, label]) => (
                        <div
                            key={v}
                            onClick={() => setSurfaceStyle(v)}
                            style={{
                                padding: c ? '3px 4px' : '4px 0',
                                borderRadius: 4,
                                textAlign: 'center',
                                cursor: 'pointer',
                                fontSize: c ? 10 : 11,
                                fontWeight: 600,
                                transition: 'all 0.15s',
                                flex: row ? 1 : undefined,
                                alignSelf: col ? 'stretch' : undefined,
                                background: surfaceStyle === v ? 'rgba(24,144,255,0.85)' : 'rgba(255,255,255,0.06)',
                                color: surfaceStyle === v ? '#fff' : 'rgba(255,255,255,0.6)',
                                border: `1px solid ${
                                    surfaceStyle === v ? 'rgba(24,144,255,0.5)' : 'rgba(255,255,255,0.08)'
                                }`,
                            }}
                        >
                            {label}
                        </div>
                    ))}
                </div>
            </div>

            {/* Measure - compact 2-col grid */}
            <div
                style={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: c ? 6 : 8,
                    marginBottom: c ? 6 : 8,
                }}
            >
                <div
                    style={{
                        fontSize: c ? 9 : 10,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.3)',
                        textTransform: 'uppercase',
                        letterSpacing: c ? 0.5 : 1,
                        marginBottom: c ? 5 : 6,
                    }}
                >
                    Measure
                </div>
                <div
                    style={
                        row
                            ? { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 3 }
                            : col
                            ? { display: 'flex', flexDirection: 'column', gap: 3 }
                            : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }
                    }
                >
                    {[
                        ['ruler', 'Distance', 'Dst', <NodeIndexOutlined key="r" />, measurements.length] as const,
                        ['edge', 'Edge', 'Edg', <NodeIndexOutlined key="e" />, edgeMeasurements.length] as const,
                        [
                            'edgeDist',
                            'Edge to Edge',
                            'E2E',
                            <NodeIndexOutlined key="ed" />,
                            edgeDistMeasurements.length,
                        ] as const,
                        ['angle', 'Angle', 'Ang', <ColumnHeightOutlined key="a" />, angleMeasurements.length] as const,
                        ['thickness', 'Thick.', 'Thk', <CompressOutlined key="t" />, thickMeasurements.length] as const,
                        ['area', 'Area', 'Ar', <BorderOuterOutlined key="ar" />, areaMeasurements.length] as const,
                    ].map(([tool, label, short, icon, count]) => (
                        <div
                            key={tool}
                            onClick={() => toggleTool(tool as any)}
                            style={{
                                padding: c ? '4px 5px' : '5px 4px',
                                borderRadius: 4,
                                textAlign: 'center',
                                cursor: 'pointer',
                                fontSize: c ? 10 : 11,
                                fontWeight: 600,
                                transition: 'all 0.15s',
                                position: 'relative',
                                flex: row ? '1 1 calc(50% - 2px)' : undefined,
                                minWidth: row ? 0 : undefined,
                                maxWidth: row ? 'calc(50% - 2px)' : undefined,
                                boxSizing: row ? 'border-box' : undefined,
                                alignSelf: col ? 'stretch' : undefined,
                                background: activeTool === tool ? 'rgba(24,144,255,0.85)' : 'rgba(255,255,255,0.06)',
                                color: activeTool === tool ? '#fff' : 'rgba(255,255,255,0.6)',
                                border: `1px solid ${
                                    activeTool === tool ? 'rgba(24,144,255,0.5)' : 'rgba(255,255,255,0.08)'
                                }`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: c ? 2 : 4,
                                flexWrap: row ? 'wrap' : undefined,
                            }}
                        >
                            {icon}
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: row ? 'nowrap' : undefined,
                                }}
                            >
                                {label}
                            </span>
                            {(count as number) > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: -4,
                                        right: -4,
                                        background: '#ff4d4f',
                                        color: '#fff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        borderRadius: 8,
                                        minWidth: 16,
                                        height: 16,
                                        lineHeight: '16px',
                                        textAlign: 'center',
                                        padding: '0 4px',
                                    }}
                                >
                                    {count as number}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
                {(measurements.length > 0 ||
                    edgeMeasurements.length > 0 ||
                    edgeDistMeasurements.length > 0 ||
                    angleMeasurements.length > 0 ||
                    thickMeasurements.length > 0 ||
                    areaMeasurements.length > 0) && (
                    <div
                        onClick={() => {
                            setMeasurements([]);
                            setPendingPoint(null);
                            setEdgeMeasurements([]);
                            setEdgeDistMeasurements([]);
                            setEdgeDistPending(null);
                            setHoveredEdgeIdx(-1);
                            setAngleMeasurements([]);
                            setAnglePending(null);
                            setThickMeasurements([]);
                            setAreaMeasurements([]);
                        }}
                        style={{
                            marginTop: 4,
                            padding: '3px 0',
                            borderRadius: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'rgba(255,100,100,0.8)',
                            border: '1px solid rgba(255,100,100,0.2)',
                            transition: 'all 0.15s',
                        }}
                    >
                        Clear All
                    </div>
                )}

                {/* Measurements list */}
                {(measurements.length > 0 ||
                    edgeMeasurements.length > 0 ||
                    edgeDistMeasurements.length > 0 ||
                    angleMeasurements.length > 0 ||
                    thickMeasurements.length > 0 ||
                    areaMeasurements.length > 0) && (
                    <div
                        style={{
                            marginTop: 6,
                            maxHeight: 150,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {measurements.map((m, i) => (
                            <div
                                key={`d${i}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.04)',
                                    fontSize: 11,
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: MEASURE_COLORS[i % MEASURE_COLORS.length],
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>Distance {i + 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 'auto', fontWeight: 600 }}>
                                    {fmt(m.dist)}
                                </span>
                                <span
                                    onClick={() => setMeasurements(prev => prev.filter((_, j) => j !== i))}
                                    style={{
                                        cursor: 'pointer',
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                        {edgeMeasurements.map((m, i) => (
                            <div
                                key={`e${i}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.04)',
                                    fontSize: 11,
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: EDGE_COLORS[i % EDGE_COLORS.length],
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>Edge {i + 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 'auto', fontWeight: 600 }}>
                                    {m.radius ? `R${fmt(m.radius)}` : fmt(m.length)}
                                </span>
                                <span
                                    onClick={() => setEdgeMeasurements(prev => prev.filter((_, j) => j !== i))}
                                    style={{
                                        cursor: 'pointer',
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                        {edgeDistMeasurements.map((m, i) => (
                            <div
                                key={`ed${i}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.04)',
                                    fontSize: 11,
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: EDGE_DIST_COLORS[i % EDGE_DIST_COLORS.length],
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>E2E {i + 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 'auto', fontWeight: 600 }}>
                                    {fmt(m.dist)}
                                </span>
                                <span
                                    onClick={() => setEdgeDistMeasurements(prev => prev.filter((_, j) => j !== i))}
                                    style={{
                                        cursor: 'pointer',
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                        {angleMeasurements.map((m, i) => (
                            <div
                                key={`a${i}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.04)',
                                    fontSize: 11,
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: ANGLE_COLORS[i % ANGLE_COLORS.length],
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>Angle {i + 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 'auto', fontWeight: 600 }}>
                                    {m.angle.toFixed(1)}°
                                </span>
                                <span
                                    onClick={() => setAngleMeasurements(prev => prev.filter((_, j) => j !== i))}
                                    style={{
                                        cursor: 'pointer',
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                        {thickMeasurements.map((m, i) => (
                            <div
                                key={`t${i}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.04)',
                                    fontSize: 11,
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: THICK_COLORS[i % THICK_COLORS.length],
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>Thick. {i + 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 'auto', fontWeight: 600 }}>
                                    {fmt(m.value)}
                                </span>
                                <span
                                    onClick={() => setThickMeasurements(prev => prev.filter((_, j) => j !== i))}
                                    style={{
                                        cursor: 'pointer',
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                        {areaMeasurements.map((m, i) => (
                            <div
                                key={`ar${i}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.04)',
                                    fontSize: 11,
                                }}
                            >
                                <div
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: AREA_COLORS[i % AREA_COLORS.length],
                                        flexShrink: 0,
                                    }}
                                />
                                <span style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>Area {i + 1}</span>
                                <span style={{ color: 'rgba(255,255,255,0.85)', marginLeft: 'auto', fontWeight: 600 }}>
                                    {fmtArea(m.value)}
                                </span>
                                <span
                                    onClick={() => setAreaMeasurements(prev => prev.filter((_, j) => j !== i))}
                                    style={{
                                        cursor: 'pointer',
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: 13,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Cross Section */}
            <div
                style={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: c ? 6 : 8,
                    marginBottom: c ? 6 : 8,
                }}
            >
                <div
                    onClick={() =>
                        setClip(p =>
                            p.enabled
                                ? { ...p, enabled: false }
                                : {
                                      ...p,
                                      enabled: true,
                                      axis: 'z',
                                      value: bounds ? bounds.max.z : 0,
                                      faceNormal: undefined,
                                      faceRange: undefined,
                                  }
                        )
                    }
                    style={{
                        padding: c ? '4px 6px' : '5px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: c ? 10 : 11,
                        fontWeight: 600,
                        transition: 'all 0.15s',
                        background: clip.enabled ? 'rgba(24,144,255,0.85)' : 'rgba(255,255,255,0.06)',
                        color: clip.enabled ? '#fff' : 'rgba(255,255,255,0.6)',
                        border: `1px solid ${clip.enabled ? 'rgba(24,144,255,0.5)' : 'rgba(255,255,255,0.08)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <ScissorOutlined /> Cross Section
                </div>

                {clip.enabled && bounds && (
                    <div style={{ marginTop: c ? 5 : 6 }}>
                        <div
                            style={
                                row
                                    ? { display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 3 }
                                    : col
                                    ? { display: 'flex', flexDirection: 'column', gap: 3 }
                                    : { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 3 }
                            }
                        >
                            {(['x', 'y', 'z'] as const).map(a => (
                                <div
                                    key={a}
                                    onClick={() =>
                                        setClip(p => ({
                                            ...p,
                                            axis: a,
                                            value: getClipInitialValue(a, bounds),
                                            faceNormal: undefined,
                                            faceRange: undefined,
                                        }))
                                    }
                                    style={{
                                        padding: c ? '3px 2px' : '3px 0',
                                        borderRadius: 3,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        fontSize: c ? 10 : 11,
                                        fontWeight: 700,
                                        transition: 'all 0.15s',
                                        flex: row ? 1 : undefined,
                                        alignSelf: col ? 'stretch' : undefined,
                                        background:
                                            clip.axis === a ? 'rgba(24,144,255,0.85)' : 'rgba(255,255,255,0.06)',
                                        color: clip.axis === a ? '#fff' : 'rgba(255,255,255,0.6)',
                                        border: `1px solid ${
                                            clip.axis === a ? 'rgba(24,144,255,0.5)' : 'rgba(255,255,255,0.08)'
                                        }`,
                                    }}
                                >
                                    {a.toUpperCase()}
                                </div>
                            ))}
                            <div
                                onClick={() =>
                                    setClip(p => ({
                                        ...p,
                                        axis: 'face' as const,
                                        faceNormal: undefined,
                                        faceRange: undefined,
                                        value: 0,
                                    }))
                                }
                                style={{
                                    padding: c ? '3px 2px' : '3px 0',
                                    borderRadius: 3,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    fontSize: c ? 10 : 11,
                                    fontWeight: 700,
                                    transition: 'all 0.15s',
                                    flex: row ? 1 : undefined,
                                    alignSelf: col ? 'stretch' : undefined,
                                    background:
                                        clip.axis === 'face' ? 'rgba(24,144,255,0.85)' : 'rgba(255,255,255,0.06)',
                                    color: clip.axis === 'face' ? '#fff' : 'rgba(255,255,255,0.6)',
                                    border: `1px solid ${
                                        clip.axis === 'face' ? 'rgba(24,144,255,0.5)' : 'rgba(255,255,255,0.08)'
                                    }`,
                                }}
                            >
                                Face
                            </div>
                        </div>
                        {clip.axis === 'face' && !clip.faceNormal && (
                            <div
                                style={{
                                    marginTop: 4,
                                    fontSize: 10,
                                    color: 'rgba(255,255,255,0.5)',
                                    textAlign: 'center',
                                }}
                            >
                                Click a face to set section plane
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    return (
        <div ref={rootRef} style={{ display: 'flex', width: '100%', height: '100%', position: 'relative' }}>
            <style>{`@keyframes pulse-hint { 0%,100% { opacity: 0.6; transform: translateY(-50%) rotate(-90deg) translateX(0); } 50% { opacity: 1; transform: translateY(-50%) rotate(-90deg) translateX(-2px); } }`}</style>
            {(layoutMode === 'normal' || layoutMode === 'compact') && (
                <div
                    style={{
                        width: sideW,
                        minWidth: sideW,
                        padding: `${sidePad}px ${sidePad}px`,
                        background: 'linear-gradient(180deg, #2a3140 0%, #232a36 100%)',
                        borderRight: '1px solid rgba(255,255,255,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flexShrink: 0,
                    }}
                >
                    {renderSidebarPanel()}
                </div>
            )}

            {/* 3D Canvas with QuickTurn-style background */}
            <div
                ref={canvasContainerRef}
                style={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle, rgba(82,89,94,1) 0%, rgba(46,53,60,1) 100%)',
                }}
            >
                {loading && (
                    <div
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            background: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff' }}>
                            <LoadingOutlined style={{ fontSize: 28 }} />
                            <span style={{ marginTop: 10 }}>Loading 3D Model...</span>
                        </div>
                    </div>
                )}
                {layoutMode === 'collapsed' && !sidebarOverlayOpen && (
                    <div
                        style={{
                            position: 'absolute',
                            left: 10,
                            top: 58,
                            zIndex: 22,
                            maxWidth: 82,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            alignItems: 'flex-start',
                            pointerEvents: 'auto',
                        }}
                    >
                        <Tooltip
                            placement="right"
                            title="Viewer is narrow: options open in a new window when the browser allows it; otherwise a panel you can drag anywhere on the page."
                        >
                            <div
                                onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openCollapsedOptions();
                                }}
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    background: 'rgba(0,0,0,0.55)',
                                    border: '1px solid rgba(255,255,255,0.22)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#fff',
                                    fontSize: 17,
                                }}
                            >
                                <MenuUnfoldOutlined />
                            </div>
                        </Tooltip>
                    </div>
                )}
                {/* Unit toggle */}
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        zIndex: 2,
                        background: 'rgba(0,0,0,0.5)',
                        borderRadius: 6,
                        padding: '5px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        backdropFilter: 'blur(4px)',
                    }}
                >
                    <span
                        style={{ fontSize: 11, fontWeight: 600, color: useInches ? 'rgba(255,255,255,0.4)' : '#fff' }}
                    >
                        mm
                    </span>
                    <Switch size="small" checked={useInches} onChange={v => setUseInches(v)} />
                    <span
                        style={{ fontSize: 11, fontWeight: 600, color: useInches ? '#fff' : 'rgba(255,255,255,0.4)' }}
                    >
                        in
                    </span>
                </div>

                {clip.enabled && bounds && (clip.axis !== 'face' || clip.faceNormal) && (
                    <div
                        style={{
                            position: 'absolute',
                            right: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            height: '60%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 600 }}>
                            {clip.axis === 'face' ? 'FACE' : clip.axis.toUpperCase()}
                        </span>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <Slider
                                vertical
                                min={slMin}
                                max={slMax}
                                step={slStep}
                                value={clip.value}
                                onChange={v => {
                                    setClipSliding(true);
                                    setClip(p => ({ ...p, value: v }));
                                }}
                                onAfterChange={() => setClipSliding(false)}
                                style={{ height: '100%' }}
                                trackStyle={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
                                handleStyle={{ borderColor: '#fff' }}
                                railStyle={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                            />
                        </div>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{clip.value.toFixed(1)}</span>
                        {clipIsAtInitial && (
                            <div
                                style={{
                                    position: 'absolute',
                                    right: -30,
                                    top: '50%',
                                    transform: 'translateY(-50%) rotate(-90deg)',
                                    transformOrigin: 'center center',
                                    background: 'rgba(0,0,0,0.7)',
                                    borderRadius: 6,
                                    padding: '6px 8px',
                                    color: '#fff',
                                    fontSize: 11,
                                    whiteSpace: 'nowrap',
                                    backdropFilter: 'blur(4px)',
                                    pointerEvents: 'none',
                                    animation: 'pulse-hint 2s ease-in-out infinite',
                                }}
                            >
                                ← Drag the slider to cut
                            </div>
                        )}
                    </div>
                )}

                <div
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        zIndex: 2,
                        display: 'flex',
                        gap: 3,
                        background: 'rgba(0,0,0,0.4)',
                        borderRadius: 5,
                        padding: 3,
                        backdropFilter: 'blur(4px)',
                    }}
                >
                    {(['X', 'Y', 'Z'] as const).map((a, i) => (
                        <div
                            key={a}
                            onClick={() =>
                                setExtraRotation(prev => {
                                    const next: [number, number, number] = [...prev];
                                    next[i] += Math.PI / 2;
                                    return next;
                                })
                            }
                            style={{
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'rgba(255,255,255,0.7)',
                                border: '1px solid rgba(255,255,255,0.15)',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                            ↻{a}
                        </div>
                    ))}
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)', margin: '2px 1px' }} />
                    <div
                        onClick={() => setShowGrid(g => !g)}
                        title={showGrid ? 'Hide Grid' : 'Show Grid'}
                        style={{
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 14,
                            color: showGrid ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)',
                            border: '1px solid rgba(255,255,255,0.15)',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                        {showGrid ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </div>
                    <div
                        onClick={handleReset}
                        title="Reset View"
                        style={{
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.7)',
                            border: '1px solid rgba(255,255,255,0.15)',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                        <UndoOutlined />
                    </div>
                </div>

                {(clip.enabled || activeTool || isMultiBody) && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 56,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            background: 'rgba(0,0,0,0.6)',
                            borderRadius: 6,
                            padding: '6px 14px',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: 11,
                            pointerEvents: 'none',
                            backdropFilter: 'blur(4px)',
                        }}
                    >
                        {clip.enabled &&
                            clip.axis === 'face' &&
                            !clip.faceNormal &&
                            'Click a face to define the cross-section plane'}
                        {clip.enabled &&
                            clip.axis === 'face' &&
                            clip.faceNormal &&
                            'Shift + Scroll to move · Double-click a face to change plane'}
                        {clip.enabled && clip.axis !== 'face' && 'Shift + Scroll to move cross section'}
                        {activeTool === 'ruler' && 'Click two points to measure — repeat for multiple'}
                        {activeTool === 'angle' && anglePending === null && 'Click first edge — repeat for more angles'}
                        {activeTool === 'angle' && anglePending !== null && 'Click second edge (0°–180° at a corner)'}
                        {activeTool === 'thickness' && 'Click any face to measure wall thickness — repeat for multiple'}
                        {activeTool === 'area' && 'Click faces to measure areas — click again to remove'}
                        {activeTool === 'edge' && 'Click near an edge to measure its length'}
                        {activeTool === 'edgeDist' && !edgeDistPending && 'Click an edge to start measuring distance'}
                        {activeTool === 'edgeDist' &&
                            edgeDistPending !== null &&
                            'Click a second edge to measure the distance'}
                        {!clip.enabled &&
                            !activeTool &&
                            isMultiBody &&
                            `Multi-body: ${bodies.filter(b => b.visible).length}/${bodies.length} visible`}
                    </div>
                )}
                <Canvas
                    dpr={typeof window !== 'undefined' ? window.devicePixelRatio : 1}
                    shadows
                    gl={CANVAS_GL}
                    style={CANVAS_STYLE_ABSOLUTE}
                    resize={{ scroll: true, debounce: { scroll: 0, resize: 0 } }}
                    onCreated={({ gl }) => {
                        r3fGlRef.current = gl;
                        gl.domElement.style.cursor = activeToolRef.current === 'thickness' ? 'crosshair' : '';
                    }}
                >
                    <CanvasParentResizeSync />
                    {/* Use an orthographic default camera for the main scene */}
                    <OrthographicCamera makeDefault position={ORTHO_CAMERA_POSITION} zoom={18} near={0.1} far={1000} />
                    <Suspense fallback={null}>
                        <Viewcube orbitTargetRef={orbitTargetRef} />
                        <Environment files="/hdr/empty_warehouse_01_1k.hdr" />
                    </Suspense>
                    <EnableLocalClipping />
                    <Controls
                        partRef={partRef}
                        modelShapes={shapes}
                        fitEpoch={modelFitEpoch}
                        onResetRef={resetRef}
                        orbitTargetRef={orbitTargetRef}
                        orbitingRef={orbitingRef}
                        lastOrbitEndRef={lastOrbitEndRef}
                        cameraRef={cameraRef}
                    />
                    <RefitInvalidator fitEpoch={modelFitEpoch} loading={loading} />
                    <MeasureTool
                        active={activeTool === 'ruler'}
                        measurements={measurements}
                        pendingPoint={pendingPoint}
                        fmt={fmt}
                    />
                    <ThicknessTool active={activeTool === 'thickness'} measurements={thickMeasurements} fmt={fmt} />
                    <AreaTool active={activeTool === 'area'} measurements={areaMeasurements} fmtArea={fmtArea} />

                    <hemisphereLight intensity={0.5} position={HEMISPHERE_LIGHT_POSITION} />
                    <directionalLight color={0xbbbbbb} intensity={0.5} position={DIRECTIONAL_LIGHT_POSITION} />
                    <pointLight position={POINT_LIGHT_POSITION} intensity={0.5} />

                    <group rotation={extraRotation}>
                        <group
                            ref={partRef}
                            onPointerDown={handlePointerDown}
                            onClick={handleClick}
                            onDoubleClick={handleDoubleClick}
                            onPointerMove={handlePointerMove}
                            onPointerLeave={handlePointerLeave}
                        >
                            {shapes.map((s, i) => {
                                const bi = shapeBodyIdx[i];
                                const body = bi >= 0 ? bodies[bi] : null;
                                if (body && !body.visible) return null;
                                return (
                                    <Shape
                                        key={s.uuid}
                                        shape={s}
                                        viewMode={viewMode}
                                        clipPlanes={clipPlanes}
                                        showCap={!clipIsAtInitial}
                                        bodyColor={body?.color}
                                        surfaceStyle={surfaceStyle}
                                    />
                                );
                            })}
                            {edges.map((e, i) => {
                                const bi = edgeBodyIdx[i];
                                const body = bi >= 0 ? bodies[bi] : null;
                                if (body && !body.visible) return null;
                                return <ModelEdge key={e.uuid} edge={e} visible={showEdges} clipPlanes={clipPlanes} />;
                            })}
                            {areaMeasurements.map((m, i) => (
                                <mesh key={i} geometry={m.highlight} raycast={() => {}}>
                                    <meshBasicMaterial
                                        color={AREA_COLORS[i % AREA_COLORS.length]}
                                        transparent
                                        opacity={0.35}
                                        side={THREE.DoubleSide}
                                        depthWrite={false}
                                    />
                                </mesh>
                            ))}
                            {activeTool === 'edge' &&
                                hoveredEdgeIdx >= 0 &&
                                !edgeMeasurements.some(m => m.groupedEdgeIndices.includes(hoveredEdgeIdx)) &&
                                edges[hoveredEdgeIdx] && (
                                    <FatEdgeHighlight geometry={edges[hoveredEdgeIdx]} color="#ffffff" />
                                )}
                            {activeTool === 'edgeDist' &&
                                hoveredEdgeIdx >= 0 &&
                                hoveredEdgeIdx !== edgeDistPending &&
                                !edgeDistMeasurements.some(
                                    m => m.edgeIdx1 === hoveredEdgeIdx || m.edgeIdx2 === hoveredEdgeIdx
                                ) &&
                                edges[hoveredEdgeIdx] && (
                                    <FatEdgeHighlight geometry={edges[hoveredEdgeIdx]} color="#ffffff" />
                                )}
                            {activeTool === 'angle' &&
                                hoveredEdgeIdx >= 0 &&
                                hoveredEdgeIdx !== anglePending &&
                                !angleMeasurements.some(
                                    m => m.edgeIdx1 === hoveredEdgeIdx || m.edgeIdx2 === hoveredEdgeIdx
                                ) &&
                                edges[hoveredEdgeIdx] && (
                                    <FatEdgeHighlight geometry={edges[hoveredEdgeIdx]} color="#ffffff" />
                                )}
                            <EdgeLengthTool active={activeTool === 'edge'} measurements={edgeMeasurements} fmt={fmt} />
                            <EdgeDistTool
                                active={activeTool === 'edgeDist'}
                                measurements={edgeDistMeasurements}
                                pendingIdx={edgeDistPending}
                                edges={edges}
                                fmt={fmt}
                            />
                            <AngleTool
                                active={activeTool === 'angle'}
                                measurements={angleMeasurements}
                                pendingIdx={anglePending}
                                edges={edges}
                            />
                            {clip.enabled && !clipIsAtInitial && viewMode !== 'wireframe' && bounds && (
                                <StencilCapPlane clip={clip} bounds={bounds} />
                            )}
                        </group>
                        {bounds && showGrid && <FloorGridGroup bounds={bounds} />}
                        {bounds && clip?.enabled && <SceneAxesIndicator bounds={bounds} />}
                    </group>
                </Canvas>
            </div>

            {layoutMode === 'collapsed' &&
                sidebarOverlayOpen &&
                optionsPortalDoc?.body &&
                createPortal(
                    <div
                        style={
                            optionsInPopup
                                ? {
                                      width: '100%',
                                      minHeight: '100vh',
                                      boxSizing: 'border-box',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      background: 'linear-gradient(180deg, #2a3140 0%, #232a36 100%)',
                                      overflow: 'hidden',
                                      pointerEvents: 'auto',
                                  }
                                : {
                                      position: 'fixed',
                                      left: optionsPanelPos.x,
                                      top: optionsPanelPos.y,
                                      width: optionsPanelWidth,
                                      maxHeight: 'min(42.5vh, 280px)',
                                      zIndex: 2147483646,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      background: 'linear-gradient(180deg, #2a3140 0%, #232a36 100%)',
                                      borderRadius: 8,
                                      boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                                      border: '1px solid rgba(255,255,255,0.12)',
                                      overflow: 'hidden',
                                      pointerEvents: 'auto',
                                  }
                        }
                    >
                        <div
                            onMouseDown={optionsInPopup ? undefined : onOptionsPanelHeaderDown}
                            style={{
                                cursor: optionsInPopup ? 'default' : 'grab',
                                flexShrink: 0,
                                padding: '10px 12px',
                                background: '#2e3644',
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                userSelect: 'none',
                            }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
                                Viewer options
                            </span>
                            <span
                                role="button"
                                onMouseDown={e => e.stopPropagation()}
                                onClick={() => closeCollapsedOptions()}
                                style={{
                                    cursor: 'pointer',
                                    padding: '2px 8px',
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: 18,
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </span>
                        </div>
                        <div style={{ overflowY: 'auto', overflowX: 'hidden', padding: 12, flex: 1, minHeight: 0 }}>
                            {renderSidebarPanel()}
                        </div>
                    </div>,
                    optionsPortalDoc.body
                )}
        </div>
    );
};

export default EnhancedViewer;
